import { useEffect, useLayoutEffect, useState, useRef, useMemo, useCallback } from "react"
import type { MouseEvent as ReactMouseEvent } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { ActionTypes } from "@/actions"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { DataTable } from "./DataTable"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import EditItem from "./EditItem"
import { registerItemEditorNavigationGuard } from "./itemEditorNavigationGuard"
import {
  TableCellTyped,
  getRelationDisplayValue,
  ensureRelationLabelsLoaded,
  relationRawToExportIds,
  getUserUidDisplayLabel,
  getUserUidDisplayNameAndEmail,
  ensureUserUidCachedFromAuth,
  hydrateUserDisplayCacheFromAuthForUids,
  preloadUsersRelationLabels,
  looksLikeRelationId,
} from "./TableHelper.tsx"
import { createColumnHelper, type VisibilityState } from "@tanstack/react-table"
import ActionMenu from "./ActionMenu"
import QuickAdd from "../QuickAdd"
import { Code2, ListChecks, ChevronDown, ChevronLeft, ChevronRight, Copy, Download, FilterX, Loader2, Upload, ScrollText, Home, Settings2, FileSpreadsheet, Info, Layers2, ArrowDownUp, Archive, Check, GitCompare } from "lucide-react"
import { useAuth } from "../auth/AuthContext"
import { impersonateUserByUid } from "../auth/impersonateUser"
import {
  resolveCollectionIdentifier,
  getMongoCollectionNameForFolder,
  buildAdminMongoDocumentUrl,
  mongoCollectionNameForInspector,
} from "../utils/collectionUtils"
import { buildCollectionPath, buildCollectionTableKey, normalizeOrgSegment } from "../utils/collectionPaths"
import { normalizeCollectionOwnerId, normalizeCreatorId } from "../utils/collectionInfoIds"
import { joinCsvRow } from "@/utils/csvExport"
import { usePermission, usePermissions, clearPermissionCache } from "../acl/usePermission"
import {
  buildItemPermissionBulkRequests,
  mergeRowsForItemPermissionChecks,
} from "../acl/buildItemPermissionBulkRequests"
import {
  buildItemPermissionsById,
  createGetItemPermissionFlags,
  createIsItemPermissionPending,
  deriveItemPermissionActions,
  type CollectionAclState,
} from "../acl/itemMenuPermissions"
import type { PermissionCheckRequest } from "../api/aclApi"
import { actionsAPI, ActionDefinition } from "../api/actionsApi"

import { Checkbox } from "@/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
// Import correct Dialog components from shadcn UI
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Switch } from "@/components/ui/switch"
import ExcelJS from "exceljs"
import { dataAPI, type ActivityLogEntry } from "../api/dataApi"
import { authAPI, type User } from "../api/authApi"
import {
  fetchItemByIdAPI,
  invalidateItemsCacheForSlug,
  clearDelegatedItemOwnerAPI,
  pickAndInformSubmissionsAPI,
  archiveItemAPI,
  restoreItemAPI,
} from "../api/itemsApi"
import { toast } from "react-toastify"
import { EMPTY_ARRAY, EMPTY_OBJECT } from "@/constants/reduxStableRefs"
import {
  computeTableViewLoadingState,
  findTableEntryForRoute,
  getTableLoadingStateForRoute,
  getTableSortingForRoute,
} from "./tableViewUtils"
import {
  ComponentTableInlineDataLoading,
  ComponentTablePageLoading,
} from "./componentTable/ComponentTableLoadingState"
import { ComponentTableDataDialogs } from "./componentTable/ComponentTableDataDialogs"
import { ComponentTableItemOverlays } from "./componentTable/ComponentTableItemOverlays"
import { getFlexibleFieldValue, getRowSummaryFieldNamesFromMeta } from "./buildRowFieldSummary"
import { useComponentTableColumns } from "./componentTable/useComponentTableColumns"
import { useComponentTableFilters } from "./componentTable/useComponentTableFilters"
import { buildTableRequestPayload, buildTableSortParams } from "./componentTable/buildTableRequestPayload"
import { fetchAllCollectionItemsForExport } from "./componentTable/fetchAllCollectionItemsForExport"
import { resolveItemListPage } from "../api/itemsApi"
import { parseTableTimestamp } from "./componentTable/parseTableTimestamp"
import { fetchFormConfigs, fetchFormConfig } from "../api/formConfigApi"
import {
  hasDelegatedOwner,
  resolveItemCreatorUid,
  resolveItemOwnerUid,
  uidsEqual,
} from "./resolveItemOwnerUid"
import { formatErrorForDisplay } from "@/utils/errorMessage"
import { isFormSubmissionRow, validatedUserDetailRows } from "../utils/formSubmissionRowUtils"
import { cn } from "@/lib/utils"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { isItemArchived, type ArchivedListMode } from "@/utils/itemArchive"
import {
  findColumnView,
  readSessionColumnView,
  rememberSessionColumnView,
  type ColumnViewPref,
} from "./columnViewPrefs"
import NotFoundPage from "../system/NotFoundPage"

function parseStoredColumnPrefs(stored: string): { order: string[]; hidden: string[] } | null {
  try {
    const parsed = JSON.parse(stored) as unknown
    if (Array.isArray(parsed)) {
      const order = parsed.filter((id): id is string => typeof id === "string")
      return order.length > 0 ? { order, hidden: [] } : null
    }
    if (
      parsed &&
      typeof parsed === "object" &&
      Array.isArray((parsed as { order?: unknown }).order)
    ) {
      const raw = parsed as { order: unknown[]; hidden?: unknown }
      const order = raw.order.filter((id): id is string => typeof id === "string")
      const hidden = Array.isArray(raw.hidden)
        ? raw.hidden.filter((id): id is string => typeof id === "string")
        : []
      return order.length > 0 ? { order, hidden } : null
    }
  } catch {
    return null
  }
  return null
}

function visibilityStateFromHiddenIds(hiddenIds: string[]): VisibilityState {
  const vis: VisibilityState = {}
  for (const id of hiddenIds) {
    vis[id] = false
  }
  return vis
}

function areStringArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false
  }
  return true
}

function hiddenIdsFromVisibilityState(vis: VisibilityState): string[] {
  return Object.entries(vis)
    .filter(([, visible]) => visible === false)
    .map(([id]) => id)
    .sort()
}

function areVisibilityStatesEqual(a: VisibilityState, b: VisibilityState): boolean {
  const ah = hiddenIdsFromVisibilityState(a)
  const bh = hiddenIdsFromVisibilityState(b)
  return areStringArraysEqual(ah, bh)
}

function resolveItemFieldKey(item: Record<string, unknown> | null | undefined, fieldKey: string): string {
  const k = String(fieldKey ?? '').trim()
  if (!item || !k) return k
  if (Object.prototype.hasOwnProperty.call(item, k)) return k
  const lower = k.toLowerCase()
  const matchKey = Object.keys(item).find((rk) => String(rk).toLowerCase() === lower)
  return matchKey ?? k
}

function toBoolComparable(v: unknown): boolean | undefined {
  if (v === true || v === false) return v
  if (typeof v === 'number' && Number.isFinite(v)) return v !== 0
  const s = String(v ?? '').trim().toLowerCase()
  if (s === 'true' || s === '1' || s === 'yes') return true
  if (s === 'false' || s === '0' || s === 'no') return false
  return undefined
}

function explicitBoolFromOperationValue(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const t = value.trim().toLowerCase()
    if (t === 'true' || t === 'false') return t === 'true'
  }
  return null
}

function resolvedClearedValueForField(fieldDef: any): unknown {
  if (!fieldDef) return ""
  const t = String(fieldDef.type ?? "").toLowerCase().trim()
  if (t === "multiplerelation") return []
  if (t === "relation" || t === "systemrelation") return null
  if (t === "number") return null
  return ""
}

/** Pick-inform dialog + email: human-readable datetime (same options as backend email). */
const PICK_INFORM_DISPLAY_DATETIME: Intl.DateTimeFormatOptions = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
}

function formatPickInformDateTimeDisplay(d: Date): string {
  return new Intl.DateTimeFormat("en-US", PICK_INFORM_DISPLAY_DATETIME).format(d)
}

function stringLooksLikeSerializedDate(t: string): boolean {
  return /^\d{4}-\d{2}-\d{2}/.test(t) || /\d{4}-\d{2}-\d{2}T/.test(t)
}

function formatPickInformDetailValue(v: unknown): string {
  if (v == null || v === "") return "—"
  if (typeof v === "boolean") return v ? "Yes" : "No"
  if (typeof v === "number" && Number.isFinite(v)) return String(v)
  if (typeof Date !== "undefined" && v instanceof Date) {
    return Number.isNaN(v.getTime()) ? "—" : formatPickInformDateTimeDisplay(v)
  }
  if (typeof v === "string") {
    const t = v.trim()
    if (!t) return "—"
    if (stringLooksLikeSerializedDate(t)) {
      const d = parseTableTimestamp(t)
      if (d && !Number.isNaN(d.getTime())) return formatPickInformDateTimeDisplay(d)
    }
    return t.length > 160 ? `${t.slice(0, 159)}…` : t
  }
  if (Array.isArray(v)) {
    if (v.length === 0) return "—"
    const parts = v.map((x) => (x == null ? "" : String(x).trim())).filter(Boolean)
    const s = parts.join(", ")
    return s.length > 160 ? `${s.slice(0, 159)}…` : s
  }
  if (typeof v === "object" && v !== null && "seconds" in (v as Record<string, unknown>)) {
    const d = parseTableTimestamp(v)
    return d && !Number.isNaN(d.getTime()) ? formatPickInformDateTimeDisplay(d) : "—"
  }
  if (typeof v === "object") {
    try {
      const s = JSON.stringify(v)
      return s.length > 160 ? `${s.slice(0, 159)}…` : s
    } catch {
      return "{…}"
    }
  }
  return String(v)
}

function pickInformSubmissionRaw(row: Record<string, unknown>): unknown {
  return (
    row.createdAt ??
    row._created ??
    row.created
  )
}

function formatPickInformSubmittedAt(row: Record<string, unknown>): string {
  return formatPickInformDetailValue(pickInformSubmissionRaw(row))
}

function pickInformRowPairable(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const formA = a._formUid != null ? String(a._formUid).trim() : ""
  const formB = b._formUid != null ? String(b._formUid).trim() : ""
  if (!formA || formA !== formB) return false
  const cA = resolveItemCreatorUid(a)
  const cB = resolveItemCreatorUid(b)
  if (uidsEqual(cA, cB) && cA) return true
  const oA = resolveItemOwnerUid(a)
  const oB = resolveItemOwnerUid(b)
  if (uidsEqual(oA, oB) && oA) return true
  const eA = String(a._submitterEmail ?? "")
    .toLowerCase()
    .trim()
  const eB = String(b._submitterEmail ?? "")
    .toLowerCase()
    .trim()
  if (eA && eA === eB) return true
  return false
}

/** All rows must pair with every other (same form + same submitter identity), unique uids, at least 2. */
function pickInformGroupFromSelection(
  selectedRows: unknown[]
): Record<string, unknown>[] | null {
  if (selectedRows.length < 2) return null
  const rows = selectedRows as Record<string, unknown>[]
  const seen = new Set<string>()
  const unique: Record<string, unknown>[] = []
  for (const r of rows) {
    const uid = r?.uid != null ? String(r.uid).trim() : ""
    if (!uid || seen.has(uid)) continue
    seen.add(uid)
    unique.push(r)
  }
  if (unique.length < 2) return null
  for (let i = 0; i < unique.length; i++) {
    for (let j = i + 1; j < unique.length; j++) {
      if (!pickInformRowPairable(unique[i], unique[j])) return null
    }
  }
  return unique
}

type TableExportFormat = 'json' | 'xlsx' | 'txt' | 'semicolon' | 'csv'

const TABLE_EXPORT_FORMAT_OPTIONS: { format: TableExportFormat; label: string }[] = [
  { format: 'json', label: 'JSON' },
  { format: 'xlsx', label: 'Excel (.xlsx)' },
  { format: 'txt', label: 'Plain text' },
  { format: 'csv', label: 'CSV' },
  { format: 'semicolon', label: 'Semicolon-separated' },
]

type TableImportExportMenuItemsProps = {
  canCreate: boolean
  createPermissionLoading: boolean
  hasData: boolean
  selectionCount: number
  exportLoading: boolean
  onImport: () => void
  onExport: (scope: 'all' | 'selection', format: TableExportFormat) => void
}

const ARCHIVE_VIEW_OPTIONS: { value: ArchivedListMode; label: string; hint?: string }[] = [
  { value: "no", label: "Default", hint: "Hide archived rows" },
  { value: "all", label: "Show all", hint: "Active and archived" },
  { value: "only", label: "Archived only", hint: "Archived rows only" },
]

function TableArchiveVisibilityMenuItems({
  mode,
  onSelect,
}: {
  mode: ArchivedListMode
  onSelect: (m: ArchivedListMode) => void
}) {
  return (
    <>
      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
        Archived rows
      </DropdownMenuLabel>
      {ARCHIVE_VIEW_OPTIONS.map((opt) => (
        <DropdownMenuItem
          key={opt.value}
          onClick={() => onSelect(opt.value)}
          className="gap-2"
        >
          {mode === opt.value ? (
            <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          ) : (
            <span className="h-4 w-4 shrink-0" aria-hidden />
          )}
          <div className="flex min-w-0 flex-col gap-0.5">
            <span>{opt.label}</span>
            {opt.hint ? (
              <span className="text-xs font-normal text-muted-foreground">{opt.hint}</span>
            ) : null}
          </div>
        </DropdownMenuItem>
      ))}
    </>
  )
}

function TableImportExportMenuItems({
  canCreate,
  createPermissionLoading,
  hasData,
  selectionCount,
  exportLoading,
  onImport,
  onExport,
}: TableImportExportMenuItemsProps) {
  const showImport = !createPermissionLoading && canCreate
  return (
    <>
      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
        Import / export
      </DropdownMenuLabel>
      {showImport && (
        <DropdownMenuItem onClick={onImport} className="gap-2">
          <Upload className="h-4 w-4 shrink-0 opacity-70" />
          Import CSV / JSON / Excel…
        </DropdownMenuItem>
      )}
      {showImport && hasData ? <DropdownMenuSeparator /> : null}
      {hasData ? (
        <>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="gap-2" disabled={exportLoading}>
              <Download className="h-4 w-4 shrink-0 opacity-70" />
              Export all rows
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-48">
              {TABLE_EXPORT_FORMAT_OPTIONS.map(({ format, label }) => (
                <DropdownMenuItem
                  key={`all-${format}`}
                  disabled={exportLoading}
                  onClick={() => onExport('all', format)}
                >
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              className="gap-2 data-[disabled]:opacity-50 data-[disabled]:text-muted-foreground"
              disabled={selectionCount === 0 || exportLoading}
            >
              <Download className="h-4 w-4 shrink-0 opacity-70" />
              <span className="flex-1 text-left">Export selection</span>
              {selectionCount > 0 ? (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {selectionCount}
                </span>
              ) : null}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-48">
              {TABLE_EXPORT_FORMAT_OPTIONS.map(({ format, label }) => (
                <DropdownMenuItem
                  key={`sel-${format}`}
                  disabled={selectionCount === 0 || exportLoading}
                  onClick={() => onExport('selection', format)}
                >
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </>
      ) : null}
    </>
  )
}

const ComponentTable = () => {
  // TEMP TEST: keep table view in loading state (do not render table)
  const forceLoadingTest = false
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user: authUser } = useAuth()

  const location = useLocation()
  const navigationState = (location.state || null) as import("../nav/searchNavigationState").CollectionSearchNavigationState | null
  const initialHighlightUid = navigationState?.highlightUid || null
  const pendingSearchColumnFilterRef = useRef(
    navigationState?.searchColumnFilter ?? null
  )

  const _tables = useSelector((state: any) => state.table.tables)
  const _meta = useSelector((state: any) => state.table.meta)
  const tableLoadingBySlug = useSelector(
    (state: any) => state.table.loadingBySlug ?? EMPTY_OBJECT,
    shallowEqual
  )
  const debugMode = useSelector((state: any) => state.ui?.debugMode) || false
  const reloadConfigNonce = useSelector((state: any) => state.ui?.reloadConfigNonce ?? 0)
  const { org: routeOrgParam, slug: routeSlugParam } = useParams<{ org?: string; slug?: string }>()
  const routeCollectionSlug = (routeSlugParam || '').replace(/[!]+$/, '').trim()
  const routeOrgSegment = (routeOrgParam || '').replace(/[!]+$/, '').trim()
  const routeOrgNorm = normalizeOrgSegment(routeOrgSegment)
  const activeTableKey = buildCollectionTableKey(routeCollectionSlug, routeOrgSegment || null)

  const _currentSlugRaw: string = routeCollectionSlug
  const _currentSlug: string = _currentSlugRaw
  const components = useSelector((state: any) => state.components.data ?? EMPTY_ARRAY)
  const [allTables, setAllTables] = useState([])
  const [allMeta, setAllMeta] = useState((meta: any) => meta)
  const [currentSlug, setCurrentSlug] = useState('')
  const [table, setTable] = useState((data: any) => data)
  const [meta, setMeta] = useState((data: any) => data)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const _user = useSelector((state: any) => state.user)
  // Use auth context user if available, fallback to Redux user (same as MenuItems)
  // Memoize user to prevent unnecessary re-renders
  const user = useMemo(() => authUser || _user, [authUser, _user])
  const [columns, setColumns] = useState<any>()
  const [showEditor, setShowEditor] = useState(false)
  const editItemRef = useRef<{
    save: () => Promise<void>
    isLoading: boolean
    valid: boolean
    isNew: boolean
    hasUnsavedChanges: () => boolean
  }>(null)
  const columnHelper = useMemo(() => createColumnHelper(), [])
  const [updateList, setUpdateList] = useState(false)
  const [archivedListMode, setArchivedListModeState] = useState<ArchivedListMode>("no")
  const setArchiveViewMode = useCallback(
    (mode: ArchivedListMode) => {
      setArchivedListModeState(mode)
      try {
        localStorage.setItem(`labwick:tableArchiveView:${activeTableKey}`, mode)
      } catch {
        /* ignore */
      }
      setUpdateList(true)
    },
    [activeTableKey]
  )

  useEffect(() => {
    let v: ArchivedListMode = "no"
    try {
      const r =
        typeof localStorage !== "undefined"
          ? localStorage.getItem(`labwick:tableArchiveView:${activeTableKey}`)
          : null
      if (r === "all" || r === "only" || r === "no") {
        v = r
      }
    } catch {
      /* ignore */
    }
    setArchivedListModeState(v)
  }, [activeTableKey])

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false)
  const [userInfoDialogOpen, setUserInfoDialogOpen] = useState(false)
  const [userInfoDialogUid, setUserInfoDialogUid] = useState<string | null>(null)
  const [userInfoDialogProfile, setUserInfoDialogProfile] = useState<User | null>(null)
  const [userInfoDialogPreview, setUserInfoDialogPreview] = useState<{ name: string; email: string } | null>(null)
  const [userInfoDialogLoading, setUserInfoDialogLoading] = useState(false)
  const [formSubmissionUserDoc, setFormSubmissionUserDoc] = useState<Record<string, unknown> | null>(null)
  const [impersonatingSubmissionUid, setImpersonatingSubmissionUid] = useState<string | null>(null)
  const [selectedRows, setSelectedRows] = useState<any[]>([])
  const _currentItem = useSelector((state: any) => state.item.current)
  const [currentItem, setCurrentItem] = useState(_currentItem)
  const _currentMeta = useSelector((state: any) =>
    state.table.meta?.find((meta: any) => {
      if (meta.tableKey) {
        return meta.tableKey === activeTableKey
      }
      const mOrg = normalizeOrgSegment(meta.organization)
      return meta.slug === routeCollectionSlug && mOrg === routeOrgNorm
    })
  )
  const [currentMeta, setCurrentMeta] = useState(_currentMeta)
  const tableSorting = useSelector((state: any) =>
    getTableSortingForRoute(
      state.table.tables,
      activeTableKey,
      routeCollectionSlug,
      routeOrgNorm
    )
  )
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [formTitleByUid, setFormTitleByUid] = useState<Record<string, string>>({})

  useEffect(() => {
    const slug = String(
      (currentMeta as { slug?: string } | null)?.slug ||
      (meta as { slug?: string } | null)?.slug ||
      routeCollectionSlug ||
      ""
    ).trim()
    if (!slug) {
      setFormTitleByUid({})
      return
    }
    let cancelled = false
    void fetchFormConfigs(slug)
      .then((forms) => {
        if (cancelled) return
        const next: Record<string, string> = {}
        for (const f of forms) {
          const id = f.uid != null ? String(f.uid).trim() : ""
          if (!id) continue
          next[id] = typeof f.title === "string" ? f.title.trim() : ""
        }
        setFormTitleByUid(next)
      })
      .catch(() => {
        if (!cancelled) {
          setFormTitleByUid({})
        }
      })
    return () => {
      cancelled = true
    }
  }, [
    (currentMeta as { slug?: string } | null)?.slug,
    (meta as { slug?: string } | null)?.slug,
    routeCollectionSlug,
  ])

  const {
    activeFilterColumnId,
    setActiveFilterColumnId,
    selectFilters,
    csvFilters,
    createdRange,
    setCreatedRange,
    updatedRange,
    setUpdatedRange,
    createdRangeRef,
    updatedRangeRef,
    normalizedCsvFilters,
    serverColumnFilters,
    hasActiveFilters,
    appliedFilterCount,
    filteredRows,
    isFieldFilterApplied,
    renderFieldFilter,
    renderCsvButton,
    clearCsvForField,
    clearAllColumnFilters,
    goToPage,
    applySearchColumnFilterFromNavigation,
    applyDuplicateResultFilters,
    csvDialogOpen,
    csvDialogFieldName,
    csvDialogInput,
    setCsvDialogInput,
    handleCsvDialogOpenChange,
    applyCsvDialog,
    filtersSignature,
  } = useComponentTableFilters({
    currentSlug,
    user,
    tableData: table?.data,
    meta,
    currentMeta,
    setPage,
    setUpdateList,
    formTitleByUid,
  })
  const tableScrollRef = useRef<HTMLDivElement>(null)
  const tableToolbarRef = useRef<HTMLDivElement>(null)
  const tableScrollLeftRef = useRef(0)
  const [tableToolbarHeight, setTableToolbarHeight] = useState(0)
  const [showJsonDialog, setShowJsonDialog] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [collectionInfoDialogOpen, setCollectionInfoDialogOpen] = useState(false)
  const [actionsDialogOpen, setActionsDialogOpen] = useState(false)
  const [actionsBulkConfirmAction, setActionsBulkConfirmAction] = useState<ActionDefinition | null>(null)
  const [excelBulkUpdateOpen, setExcelBulkUpdateOpen] = useState(false)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [analyzeMenuOpen, setAnalyzeMenuOpen] = useState(false)
  const [duplicatesDialogOpen, setDuplicatesDialogOpen] = useState(false)
  const [compareDialogOpen, setCompareDialogOpen] = useState(false)
  const [pickInformDialogOpen, setPickInformDialogOpen] = useState(false)
  const [pickInformKeepUid, setPickInformKeepUid] = useState<string>("")
  const [pickInformPersonalNote, setPickInformPersonalNote] = useState("")
  const [pickInformBusy, setPickInformBusy] = useState(false)
  const [exportParams, setExportParams] = useState<{
    scope: 'all' | 'selection'
    format: 'json' | 'xlsx' | 'txt' | 'semicolon' | 'csv'
  } | null>(null)
  const [exportAllFields, setExportAllFields] = useState(true)
  const [exportSelectedFields, setExportSelectedFields] = useState<string[]>([])
  const [exportSemicolonField, setExportSemicolonField] = useState<string>('')
  const [exportRelationMode, setExportRelationMode] = useState<'labels' | 'ids'>('labels')
  const [exportLoading, setExportLoading] = useState(false)
  const [userActions, setUserActions] = useState<ActionDefinition[]>([])
  const [selectedActionValue, setSelectedActionValue] = useState<string>("")
  const [actionFlashUid, setActionFlashUid] = useState<string | null>(null)
  const [activityLogOpen, setActivityLogOpen] = useState(false)
  const [activityLogEntries, setActivityLogEntries] = useState<ActivityLogEntry[]>([])
  const [activityLogTotal, setActivityLogTotal] = useState(0)
  const [activityLogLoading, setActivityLogLoading] = useState(false)
  const [activityLogActionFilter, setActivityLogActionFilter] = useState<string>("")
  const [activityLogOrderBy, setActivityLogOrderBy] = useState<'date' | 'action'>('date')
  const [activityLogOrderDirection, setActivityLogOrderDirection] = useState<'asc' | 'desc'>('desc')
  const [activityLogPage, setActivityLogPage] = useState(0)
  const activityLogPageSize = 50
  const [activityLogGroupByUser, setActivityLogGroupByUser] = useState(true)
  const actionFlashTimeoutRef = useRef<number | null>(null)
  const [actionInProgress, setActionInProgress] = useState<
    { uid: string; field: string } | { uids: string[]; field: string } | null
  >(null)
  const actionInProgressTimeoutRef = useRef<number | null>(null)
  const [relationDialogOpen, setRelationDialogOpen] = useState(false)
  const [relationDialogTitle, setRelationDialogTitle] = useState<string>('')
  const [relationDialogLoading, setRelationDialogLoading] = useState(false)
  const [relationDialogData, setRelationDialogData] = useState<any>(null)
  const [delegateTargetItem, setDelegateTargetItem] = useState<any | null>(null)
  const [columnOrderDialogOpen, setColumnOrderDialogOpen] = useState(false)
  const [columnOrderDraft, setColumnOrderDraft] = useState<string[] | null>(null)
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [columnHiddenDraft, setColumnHiddenDraft] = useState<string[] | null>(null)
  const [columnPrefsRevision, setColumnPrefsRevision] = useState(0)
  const shippedColumnViewsRef = useRef<Set<string>>(new Set())
  const [draggingColumnId, setDraggingColumnId] = useState<string | null>(null)
  const [dragOverState, setDragOverState] = useState<{ id: string; position: "above" | "below" } | null>(null)
  const columnOrderScrollRef = useRef<HTMLDivElement | null>(null)
  const [highlightUid, setHighlightUid] = useState<string | null>(initialHighlightUid)
  const highlightPageResolveKeyRef = useRef<string>("")

  // Get component name from components list to match menu display
  const componentFromList = useMemo(() => {
    if (!components || components.length === 0) return null
    const sanitizedCurrentSlug = typeof currentSlug === 'string' ? currentSlug.replace(/[!]+$/, '').trim() : currentSlug
    return components.find((comp: any) => {
      const compSlugSanitized = typeof comp.slug === 'string' ? comp.slug.replace(/[!]+$/, '').trim() : comp.slug
      const slugMatch = compSlugSanitized === sanitizedCurrentSlug || comp.slug === sanitizedCurrentSlug
      if (!slugMatch) return false
      const co = normalizeOrgSegment(comp.organization)
      return co === routeOrgNorm
    })
  }, [components, currentSlug, routeOrgNorm])

  const componentActions = useMemo(() => {
    if (!currentSlug || userActions.length === 0) {
      return []
    }
    const slugNorm = currentSlug.replace(/[!]+$/, '').trim().toLowerCase()
    return userActions.filter((action) => {
      const a = String(action.componentSlug ?? '')
        .replace(/[!]+$/, '')
        .trim()
        .toLowerCase()
      return a === slugNorm
    })
  }, [userActions, currentSlug])

  const selectedAction = useMemo(() => {
    if (!selectedActionValue) return null
    return componentActions.find(action => (action.uid ?? action.name) === selectedActionValue) || null
  }, [componentActions, selectedActionValue])

  const actionMode = Boolean(selectedAction)
  const actionMenuActions = useMemo(() => {
    if (!componentActions.length) return []
    if (!selectedAction) return componentActions
    const selectedId = selectedAction.uid ?? selectedAction.name
    return componentActions.filter(action => (action.uid ?? action.name) !== selectedId)
  }, [componentActions, selectedAction])

  const tableSelectionTargets = useMemo(
    () => selectedRows.filter((r) => r?.uid),
    [selectedRows]
  )

  const createPermissionTargetId = useMemo(() => {
    return (
      componentFromList?.uid ||
      currentMeta?.uid ||
      meta?.uid ||
      currentSlug
    )
  }, [componentFromList?.uid, currentMeta?.uid, meta?.uid, currentSlug])

  const createPermissionRequest = useMemo<PermissionCheckRequest | null>(() => {
    if (!createPermissionTargetId) return null
    return {
      action: 'create',
      targetType: 'collection',
      targetId: createPermissionTargetId
    }
  }, [createPermissionTargetId])

  const {
    allowed: canCreate,
    loading: createPermissionLoading
  } = usePermission(createPermissionRequest, { enabled: Boolean(user && currentSlug) })

  const updatePermissionRequest = useMemo<PermissionCheckRequest | null>(() => {
    if (!createPermissionTargetId) return null
    return {
      action: "update",
      targetType: "collection",
      targetId: createPermissionTargetId,
    }
  }, [createPermissionTargetId])

  const {
    allowed: canUpdateCollection,
    loading: updatePermissionLoading,
  } = usePermission(updatePermissionRequest, { enabled: Boolean(user && currentSlug) })

  const deletePermissionTargetId = useMemo(() => {
    return (
      componentFromList?.uid ||
      currentMeta?.uid ||
      meta?.uid ||
      currentSlug
    )
  }, [componentFromList?.uid, currentMeta?.uid, meta?.uid, currentSlug])

  const deletePermissionRequest = useMemo<PermissionCheckRequest | null>(() => {
    if (!deletePermissionTargetId) return null
    return {
      action: 'delete',
      targetType: 'collection',
      targetId: deletePermissionTargetId
    }
  }, [deletePermissionTargetId])

  const {
    allowed: canDeleteCollection,
    loading: deletePermissionLoading
  } = usePermission(deletePermissionRequest, { enabled: Boolean(user && currentSlug) })

  const delegateCollectionPermissionRequest = useMemo<PermissionCheckRequest | null>(() => {
    if (!createPermissionTargetId) return null
    return {
      action: "delegate",
      targetType: "collection",
      targetId: createPermissionTargetId,
    }
  }, [createPermissionTargetId])

  const {
    allowed: canDelegateCollection,
    loading: delegateCollectionPermissionLoading,
  } = usePermission(delegateCollectionPermissionRequest, {
    enabled: Boolean(user && currentSlug),
  })

  const collectionUidForAcl = useMemo(
    () => componentFromList?.uid || currentMeta?.uid || meta?.uid || null,
    [componentFromList?.uid, currentMeta?.uid, meta?.uid]
  )

  const collectionInfoModel = useMemo((): Record<string, unknown> => {
    const merged: Record<string, unknown> = {
      ...(meta && typeof meta === "object" ? meta : {}),
      ...(currentMeta && typeof currentMeta === "object" ? currentMeta : {}),
      ...(componentFromList && typeof componentFromList === "object" ? componentFromList : {}),
    }
    const uid = (merged.uid != null && String(merged.uid).trim() !== ""
      ? String(merged.uid).trim()
      : null) || (collectionUidForAcl != null ? String(collectionUidForAcl).trim() : "")
    return { ...merged, uid }
  }, [meta, currentMeta, componentFromList, collectionUidForAcl])

  const showCollectionInfoButton = Boolean(
    !showEditor && (routeCollectionSlug || currentSlug)
  )

  const rowsForItemPermissionChecks = useMemo(() => {
    const filtered = Array.isArray(filteredRows) ? filteredRows : []
    const pageRows = filtered
    const extras: unknown[] = []
    if (currentItem) {
      extras.push(currentItem)
    }
    for (const row of selectedRows) {
      if (row) extras.push(row)
    }
    return mergeRowsForItemPermissionChecks(pageRows, extras)
  }, [filteredRows, page, pageSize, selectedRows, currentItem])

  const itemPermissionRowsKey = useMemo(() => {
    const parts: string[] = []
    for (const row of rowsForItemPermissionChecks) {
      const uid = (row as { uid?: string })?.uid
      if (uid == null || uid === "") continue
      parts.push(String(uid).trim())
    }
    parts.sort()
    return `${page}:${pageSize}|${parts.join("|")}`
  }, [rowsForItemPermissionChecks, page, pageSize])

  const collectionAclState = useMemo(
    (): CollectionAclState => ({
      canUpdateCollection,
      updatePermissionLoading,
      canDeleteCollection,
      deletePermissionLoading,
      canDelegateCollection,
      delegateCollectionPermissionLoading,
    }),
    [
      canUpdateCollection,
      updatePermissionLoading,
      canDeleteCollection,
      deletePermissionLoading,
      canDelegateCollection,
      delegateCollectionPermissionLoading,
    ]
  )

  const itemPermissionActions = useMemo(
    () => deriveItemPermissionActions(collectionAclState),
    [collectionAclState]
  )

  const needsPerItemAcl = itemPermissionActions.length > 0

  const itemPermissionConfig = useMemo(() => {
    return buildItemPermissionBulkRequests(
      rowsForItemPermissionChecks,
      collectionUidForAcl ?? undefined,
      itemPermissionActions
    )
  }, [itemPermissionRowsKey, collectionUidForAcl, itemPermissionActions, rowsForItemPermissionChecks])

  const itemPermissionChecksEnabled = Boolean(
    user && needsPerItemAcl && rowsForItemPermissionChecks.length > 0
  )

  const {
    results: itemPermissionResults,
    loading: itemPermissionsLoading
  } = usePermissions(itemPermissionConfig.requests, { enabled: itemPermissionChecksEnabled })

  const itemPermissionsById = useMemo(
    () => buildItemPermissionsById(itemPermissionConfig, itemPermissionResults),
    [itemPermissionConfig, itemPermissionResults]
  )

  const getItemPermissionFlags = useMemo(
    () => createGetItemPermissionFlags(itemPermissionsById, collectionAclState),
    [itemPermissionsById, collectionAclState]
  )

  const isItemPermissionPending = useMemo(
    () =>
      createIsItemPermissionPending(
        itemPermissionsLoading,
        collectionAclState,
        itemPermissionActions
      ),
    [itemPermissionsLoading, collectionAclState, itemPermissionActions]
  )

  const selectedDeleteUids = useMemo(() => {
    if (selectedRows.length > 0) {
      return [currentItem, ...selectedRows.filter(item => item?.uid !== currentItem?.uid)]
        .filter((v, i, a) => a.findIndex(t => t?.uid === v?.uid) === i)
        .map(item => item?.uid)
        .filter(Boolean)
    }
    return currentItem?.uid ? [currentItem.uid] : []
  }, [selectedRows, currentItem])

  const canDeleteSelected = useMemo(() => {
    if (selectedDeleteUids.length === 0) {
      return false
    }
    if (canDeleteCollection && !deletePermissionLoading) {
      return true
    }
    if (itemPermissionsLoading) {
      return false
    }
    return selectedDeleteUids.every(uid => itemPermissionsById[uid]?.canDelete)
  }, [itemPermissionsLoading, selectedDeleteUids, itemPermissionsById, canDeleteCollection, deletePermissionLoading])

  const canArchiveSelected = useMemo(() => {
    if (selectedRows.length === 0) {
      return false
    }
    const bypass = canUpdateCollection && !updatePermissionLoading
    if (!bypass && itemPermissionsLoading) {
      return false
    }
    return selectedRows.every((row: any) => {
      if (!row?.uid || isItemArchived(row)) {
        return false
      }
      if (bypass) {
        return true
      }
      return Boolean(itemPermissionsById[row.uid]?.canUpdate)
    })
  }, [
    selectedRows,
    canUpdateCollection,
    updatePermissionLoading,
    itemPermissionsLoading,
    itemPermissionsById,
  ])

  const canRestoreSelected = useMemo(() => {
    if (selectedRows.length === 0) {
      return false
    }
    const bypass = canUpdateCollection && !updatePermissionLoading
    if (!bypass && itemPermissionsLoading) {
      return false
    }
    return selectedRows.every((row: any) => {
      if (!row?.uid || !isItemArchived(row)) {
        return false
      }
      if (bypass) {
        return true
      }
      return Boolean(itemPermissionsById[row.uid]?.canUpdate)
    })
  }, [
    selectedRows,
    canUpdateCollection,
    updatePermissionLoading,
    itemPermissionsLoading,
    itemPermissionsById,
  ])

  const userIdForColumnPrefs = useMemo(() => {
    if (!user) return null
    const direct =
      (user as any).uid ||
      (user as any).id ||
      (user as any)._id ||
      (user as any).info?.uid
    return direct ? String(direct) : null
  }, [user])

  const onOwnerDelegationChanged = useCallback(() => {
    clearPermissionCache()
    if (currentSlug) {
      const orgForCache =
        (currentMeta?.organization || routeOrgSegment || '').trim() || undefined
      invalidateItemsCacheForSlug(currentSlug, orgForCache)
    }
    setUpdateList(false)
    setTimeout(() => setUpdateList(true), 100)
  }, [currentSlug, currentMeta?.organization, routeOrgSegment])

  const openDelegateOwnerDialog = useCallback((item: any) => {
    setDelegateTargetItem(item ?? null)
  }, [])

  const requestRemoveDelegatedOwner = useCallback(
    async (item: any) => {
      if (!item?.uid || !currentSlug) return
      if (!hasDelegatedOwner(item as Record<string, unknown>)) {
        toast.error("This item has no delegated owner")
        return
      }
      if (
        !window.confirm(
          "Remove delegated owner? Ownership returns to the creator"
        )
      )
        return
      try {
        await clearDelegatedItemOwnerAPI(currentSlug, item.uid, currentMeta?.organization)
        toast.success("Delegated owner removed")
        onOwnerDelegationChanged()
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to remove owner")
      }
    },
    [currentSlug, onOwnerDelegationChanged]
  )

  const rowHasDelegatedOwner = useCallback(
    (item: any) => hasDelegatedOwner(item as Record<string, unknown> | null | undefined),
    []
  )

  const getItemDelegateActionsVisible = useCallback(
    (item: any) => {
      if (!delegateCollectionPermissionLoading && canDelegateCollection) {
        return true
      }
      const uid = item?.uid
      const perms = getItemPermissionFlags(uid)
      const effectiveOwnerUid = resolveItemOwnerUid(
        item as Record<string, unknown> | null | undefined
      )
      const creatorUid = resolveItemCreatorUid(
        item as Record<string, unknown> | null | undefined
      )
      const cur = userIdForColumnPrefs
      const isEffectiveOwner = uidsEqual(effectiveOwnerUid, cur)
      const isCreator = uidsEqual(creatorUid, cur)
      return Boolean(
        perms.canDelegate || (perms.canUpdate && (isEffectiveOwner || isCreator))
      )
    },
    [
      getItemPermissionFlags,
      userIdForColumnPrefs,
      canDelegateCollection,
      delegateCollectionPermissionLoading,
    ]
  )

  const columnViewTableKey = useMemo(
    () => activeTableKey || currentSlug || "",
    [activeTableKey, currentSlug]
  )

  // Stable storage key for column order (per collection slug and user when available)
  const columnOrderStorageKey = useMemo(() => {
    const safeKey = columnViewTableKey || "global"
    const userSuffix = userIdForColumnPrefs ? `_${userIdForColumnPrefs}` : ""
    return `labwick_column_order${userSuffix}_${safeKey}`
  }, [columnViewTableKey, userIdForColumnPrefs])

  // No fallback meta - component must be found in API

  const getResolvedCollection = useCallback(() => {
    const scopedFromAll =
      Array.isArray(allMeta) ?
        allMeta.find(
          (m: any) =>
            m &&
            (m.tableKey === activeTableKey ||
              (m.slug === routeCollectionSlug && normalizeOrgSegment(m.organization) === routeOrgNorm))
        ) :
        undefined
    const candidateMeta = [meta, currentMeta, scopedFromAll]

    for (const candidate of candidateMeta) {
      const resolved = resolveCollectionIdentifier(candidate, currentSlug);
      if (resolved) {
        return resolved;
      }
    }

    return currentSlug || undefined;
  }, [meta, currentMeta, allMeta, currentSlug, activeTableKey, routeCollectionSlug, routeOrgNorm])

  const handleOnSorted = useCallback(() => {
    setPage(1)
    setUpdateList(true)
  }, [])



  useEffect(() => {
    setCurrentSlug(_currentSlug)
  }, [_currentSlug])

  useEffect(() => {
    let isMounted = true
    const loadActions = async () => {
      try {
        const response = await actionsAPI.listActions()
        const actionList = Array.isArray(response.data) ? response.data : []
        if (isMounted) {
          setUserActions(actionList)
        }
      } catch (error: any) {
        if (isMounted) {
          setUserActions([])
        }
      }
    }
    if (currentSlug) {
      loadActions()
    }
    return () => {
      isMounted = false
    }
  }, [currentSlug])

  useEffect(() => {
    if (selectedActionValue && !selectedAction) {
      setSelectedActionValue("")
    }
  }, [selectedActionValue, selectedAction])

  useEffect(() => {
    if (actionMode) {
      setSelectedRows([])
    }
  }, [actionMode])

  useEffect(() => {
    setSelectedRows([])
  }, [filtersSignature, archivedListMode, currentSlug])

  const fetchActivityLog = useCallback(async (showLoading = true) => {
    if (!currentSlug) return
    if (showLoading) setActivityLogLoading(true)
    try {
      const res = await dataAPI.getCollectionActivityLog(currentSlug, {
        action: activityLogActionFilter || undefined,
        orderBy: activityLogOrderBy,
        orderDirection: activityLogOrderDirection,
        limit: activityLogPageSize,
        offset: activityLogPage * activityLogPageSize,
        organization: currentMeta?.organization || routeOrgNorm || undefined
      })
      if (res.success && res.data) {
        setActivityLogEntries(res.data.entries)
        setActivityLogTotal(res.data.total)
      } else if (showLoading) {
        setActivityLogEntries([])
        setActivityLogTotal(0)
      }
    } catch {
      if (showLoading) {
        setActivityLogEntries([])
        setActivityLogTotal(0)
      }
    } finally {
      if (showLoading) setActivityLogLoading(false)
    }
  }, [currentSlug, currentMeta?.organization, routeOrgNorm, activityLogActionFilter, activityLogOrderBy, activityLogOrderDirection, activityLogPage])

  useEffect(() => {
    if (!activityLogOpen || !currentSlug) return

    void fetchActivityLog()
    const refreshInterval = window.setInterval(() => {
      void fetchActivityLog(false)
    }, 15_000)

    return () => window.clearInterval(refreshInterval)
  }, [activityLogOpen, currentSlug, fetchActivityLog])

  const openActivityLog = useCallback(() => {
    setActivityLogPage(0)
    setActivityLogGroupByUser(true)
    setActivityLogOpen(true)
  }, [])

  const handleActivityLogListUser = useCallback(
    (sample: ActivityLogEntry) => {
      const uid = sample.userUid?.trim()
      const email = sample.userEmail?.trim()
      if (uid) {
        applyDuplicateResultFilters([{ fieldName: "_itemOwner", comparableKey: uid }])
      } else if (!uid && !email) {
        applyDuplicateResultFilters([{ fieldName: "_itemOwner", comparableKey: "__empty__" }])
      } else {
        toast.error(
          "Table filtering uses item owner (user id). This activity entry has no user id to match."
        )
        return
      }
      setActivityLogGroupByUser(false)
      setActivityLogOpen(false)
    },
    [applyDuplicateResultFilters]
  )

  useEffect(() => {
    setCurrentItem(_currentItem)
  }, [_currentItem, showDetailsDrawer])

  useEffect(() => {
    if (typeof _currentMeta !== 'undefined') {
      setCurrentMeta(_currentMeta)
    }
  }, [_currentMeta, _currentSlug])

  const lastTableFetchKeyRef = useRef<string>("")
  const lastCompletedTableFetchKeyRef = useRef<string | null>(null)
  const isDispatchingRef = useRef<boolean>(false)
  const tableRequestWasLoadingRef = useRef<boolean>(false)
  const tabHiddenAtRef = useRef<number | null>(null)
  const tablesRef = useRef(_tables)
  tablesRef.current = _tables
  const userUidForFetch = useMemo(() => {
    if (!user || typeof user !== "object") return ""
    const u = user as { uid?: string; id?: string; info?: { uid?: string } }
    return String(u.uid || u.id || u.info?.uid || "").trim()
  }, [user])

  const tableLoadingState = useMemo(
    () =>
      getTableLoadingStateForRoute(
        tableLoadingBySlug,
        activeTableKey,
        routeCollectionSlug,
        routeOrgNorm
      ),
    [tableLoadingBySlug, activeTableKey, routeCollectionSlug, routeOrgNorm]
  )

  const tableFetchKey = useMemo(() => {
    const payload = buildTableRequestPayload({
      slugName: (_currentSlug || currentSlug || "").trim(),
      tableKey: activeTableKey,
      organization: routeOrgNorm || undefined,
      user,
      component: currentMeta,
      filters: normalizedCsvFilters,
      columnFilters: serverColumnFilters,
      archived: archivedListMode,
      page,
      pageSize,
      sorting: tableSorting,
    })
    return JSON.stringify({
      tableKey: payload.tableKey,
      pagination: payload.pagination,
      page: payload.pagination.page,
      pageSize: payload.pagination.limit,
      filters: payload.filters,
      columnFilters: payload.columnFilters ?? null,
      archived: payload.archived,
      organization: payload.organization ?? "",
      sort: payload.sort ?? null,
    })
  }, [
    _currentSlug,
    currentSlug,
    activeTableKey,
    routeOrgNorm,
    user,
    currentMeta,
    normalizedCsvFilters,
    serverColumnFilters,
    archivedListMode,
    page,
    pageSize,
    tableSorting,
  ])

  const dispatchTableRequest = useCallback(() => {
    const slugForRequest = (_currentSlug || currentSlug || "").trim()
    if (!slugForRequest || !user) return
    dispatch({
      type: ActionTypes.TABLE_REQUEST,
      payload: buildTableRequestPayload({
        slugName: slugForRequest,
        tableKey: activeTableKey,
        organization: routeOrgNorm || undefined,
        user,
        component: currentMeta,
        filters: normalizedCsvFilters,
        columnFilters: serverColumnFilters,
        archived: archivedListMode,
        page,
        pageSize,
        sorting: tableSorting,
      }),
    })
  }, [
    _currentSlug,
    currentSlug,
    activeTableKey,
    routeOrgNorm,
    user,
    currentMeta,
    normalizedCsvFilters,
    serverColumnFilters,
    archivedListMode,
    page,
    pageSize,
    tableSorting,
    dispatch,
  ])

  const dispatchTableRequestRef = useRef(dispatchTableRequest)
  dispatchTableRequestRef.current = dispatchTableRequest

  const handledReloadConfigNonceRef = useRef(0)

  useEffect(() => {
    if (!reloadConfigNonce) return
    if (handledReloadConfigNonceRef.current === reloadConfigNonce) return
    handledReloadConfigNonceRef.current = reloadConfigNonce

    lastCompletedTableFetchKeyRef.current = null
    tableRequestWasLoadingRef.current = true
    isDispatchingRef.current = true
    dispatchTableRequestRef.current()

    const timer = window.setTimeout(() => {
      isDispatchingRef.current = false
    }, 300)
    return () => window.clearTimeout(timer)
  }, [reloadConfigNonce])

  // Dispatch only when the query key changes or an explicit refresh is requested.
  // Do not depend on _tables / loading object identity (Safari re-ran this in a tight loop).
  useEffect(() => {
    const slugForRequest = (_currentSlug || currentSlug || "").trim()
    if (!slugForRequest || !userUidForFetch) return

    const fetchKeyChanged = lastTableFetchKeyRef.current !== tableFetchKey
    if (fetchKeyChanged) {
      lastCompletedTableFetchKeyRef.current = null
      tableRequestWasLoadingRef.current = false
    }

    const needsFetch =
      lastCompletedTableFetchKeyRef.current !== tableFetchKey || updateList
    if (!needsFetch) return

    // Only debounce duplicate dispatches for the same key (e.g. Safari re-runs).
    // Page/sort/filter changes must refetch even if a prior request is still in the debounce window.
    if (isDispatchingRef.current && !fetchKeyChanged) return

    isDispatchingRef.current = true
    lastTableFetchKeyRef.current = tableFetchKey
    tableRequestWasLoadingRef.current = true
    dispatchTableRequestRef.current()

    const timer = window.setTimeout(() => {
      isDispatchingRef.current = false
    }, 300)
    return () => window.clearTimeout(timer)
  }, [tableFetchKey, userUidForFetch, updateList, _currentSlug, currentSlug])

  // Mark fetch complete when Redux reports settled for our dispatched request.
  useEffect(() => {
    if (!tableRequestWasLoadingRef.current || !tableLoadingState.isSettled) return
    if (lastTableFetchKeyRef.current !== tableFetchKey) return

    tableRequestWasLoadingRef.current = false
    lastCompletedTableFetchKeyRef.current = tableFetchKey
    if (updateList) {
      setUpdateList(false)
    }
  }, [tableLoadingState.isSettled, tableFetchKey, updateList])

  const refreshList = useCallback(() => {
    lastCompletedTableFetchKeyRef.current = null
    tableRequestWasLoadingRef.current = true
    setUpdateList(true)
  }, [])

  // While any visible GPX field is recording, refresh the table every 10s.
  useEffect(() => {
    const fields = Array.isArray(currentMeta?.fields) ? currentMeta.fields : []
    const gpxFields = fields.filter((f: any) => String(f?.type || "").toLowerCase() === "gpx")
    if (gpxFields.length === 0) return
    const rows = Array.isArray(table?.data) ? table.data : []
    const anyRecording = rows.some((row: any) =>
      gpxFields.some((f: any) => row?.[f.name]?.isRecording === true)
    )
    if (!anyRecording) return
    const timer = window.setInterval(() => {
      refreshList()
    }, 10_000)
    return () => window.clearInterval(timer)
  }, [table?.data, currentMeta?.fields, refreshList])

  // Refetch after tab was hidden long enough (skip brief focus switches).
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        tabHiddenAtRef.current = Date.now()
        return
      }
      if (document.visibilityState !== "visible" || !currentSlug || !userUidForFetch) return
      // Safari can fire "visible" without a prior "hidden" (e.g. on load) — do not refetch then.
      if (tabHiddenAtRef.current == null) return
      const hiddenMs = Date.now() - tabHiddenAtRef.current
      tabHiddenAtRef.current = null
      if (hiddenMs < 60_000) return
      const orgForCache =
        (currentMeta?.organization || routeOrgSegment || "").trim() || undefined
      invalidateItemsCacheForSlug(currentSlug, orgForCache)
      setUpdateList(true)
    }
    document.addEventListener("visibilitychange", onVisibilityChange)
    return () => document.removeEventListener("visibilitychange", onVisibilityChange)
  }, [currentSlug, userUidForFetch, currentMeta?.organization, routeOrgSegment])

  useEffect(() => {
    setAllTables(_tables)
    setAllMeta(_meta)
  }, [_tables, _meta])

  const handleTableScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    tableScrollLeftRef.current = event.currentTarget.scrollLeft
  }, [])

  useLayoutEffect(() => {
    const el = tableScrollRef.current
    if (!el) return
    if (el.scrollLeft !== tableScrollLeftRef.current) {
      el.scrollLeft = tableScrollLeftRef.current
    }
  }, [page, pageSize, filteredRows, table?.data])

  const tableError = useSelector((state: any) => state.table.error)

  useEffect(() => {
    if (!tableError || typeof tableError !== 'object' || Array.isArray(tableError)) {
      return
    }
    const e = tableError as Record<string, unknown>
    const errKey =
      typeof e.tableKey === 'string' && e.tableKey.trim() ? e.tableKey.trim() : null
    const errSlug =
      typeof e.slug === 'string' && e.slug.trim()
        ? e.slug.replace(/[!]+$/, '').trim()
        : null
    if (errKey && errKey !== activeTableKey) {
      dispatch({ type: ActionTypes.CLEAR_TABLE_ERROR })
      return
    }
    if (!errKey && errSlug && errSlug !== routeCollectionSlug) {
      dispatch({ type: ActionTypes.CLEAR_TABLE_ERROR })
    }
  }, [tableError, activeTableKey, routeCollectionSlug, dispatch])

  const bulkDeleteInProgress = useSelector((state: any) => state.table.bulkDeleteInProgress === true)
  const bulkDeleteCount = useSelector((state: any) => state.table.bulkDeleteCount ?? 0)
  const prevBulkDeleteInProgressRef = useRef(false)

  useEffect(() => {
    if (prevBulkDeleteInProgressRef.current && !bulkDeleteInProgress) {
      setUpdateList((prev) => !prev)
    }
    prevBulkDeleteInProgressRef.current = bulkDeleteInProgress
  }, [bulkDeleteInProgress])

  useEffect(() => {
    const currentTable = findTableEntryForRoute(
      allTables,
      activeTableKey,
      routeCollectionSlug,
      routeOrgNorm
    )
    setTable(currentTable)

    let currentMetaFromAll =
      allMeta?.find(
        (m: any) =>
          m &&
          (m.tableKey === activeTableKey ||
            (m.slug === routeCollectionSlug &&
              normalizeOrgSegment(m.organization) === routeOrgNorm))
      ) ?? null
    if (!currentMetaFromAll && _currentMeta) {
      currentMetaFromAll = _currentMeta
    }
    setMeta(currentMetaFromAll)
  }, [
    allTables,
    allMeta,
    activeTableKey,
    routeCollectionSlug,
    routeOrgNorm,
    _currentMeta,
  ])

  const openRelationDialog = useCallback(async (args: { targetSlug: string; uid: string }) => {
    const targetSlug = args?.targetSlug
    const uid = args?.uid
    if (!targetSlug || !uid) return
    if (!looksLikeRelationId(uid)) {
      toast.error("Cannot open this relation: the cell does not contain a stored item id.")
      return
    }
    const organization =
      (currentMeta?.organization || routeOrgSegment || "").trim() || undefined
    setRelationDialogTitle(`${targetSlug}: ${uid}`)
    setRelationDialogOpen(true)
    setRelationDialogLoading(true)
    setRelationDialogData(null)
    const componentNotFound = (err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err)
      if (msg.includes("Component not found")) return true
      const st = err && typeof err === "object" && "status" in err ? (err as { status?: number }).status : undefined
      return st === 404 && /not\s+found/i.test(msg)
    }
    try {
      let item
      try {
        item = await fetchItemByIdAPI(targetSlug, uid, organization)
      } catch (first: unknown) {
        if (organization && componentNotFound(first)) {
          item = await fetchItemByIdAPI(targetSlug, uid, undefined)
        } else {
          throw first
        }
      }
      setRelationDialogData(item)
    } catch (e: any) {
      const msg = e?.message || String(e)
      toast.error(`Failed to load related item: ${msg}`)
    } finally {
      setRelationDialogLoading(false)
    }
  }, [currentMeta?.organization, routeOrgSegment])

  // Use component name from components list (matches menu), fallback to meta name, then slug
  const displayName = componentFromList?.name || meta?.name || currentSlug.replace(/-/g, ' ')

  // Check if quick add is configured for this component
  const isQuickAddConfigured = useMemo(() => {
    if (!meta || !meta.fields) return false
    const fields = Array.isArray(meta.fields) ? meta.fields : []
    const isTrue = (value: any) => value === true || value === 'true' || value === 1
    return fields.some((f: any) => isTrue(f.quickDefault))
  }, [meta])

  const showQuickAdd = isQuickAddConfigured && !createPermissionLoading && canCreate

  const isAdmin = Boolean(user?.admin || user?.info?.admin)

  useEffect(() => {
    // Update highlight when navigation state changes (e.g. new search result)
    if (navigationState && typeof navigationState.highlightUid !== 'undefined') {
      highlightPageResolveKeyRef.current = ""
      setHighlightUid(navigationState.highlightUid || null)
    }
  }, [navigationState?.highlightUid])

  useEffect(() => {
    const pending = pendingSearchColumnFilterRef.current
    if (!pending?.field || !pending?.value) return
    const fields =
      (meta as { fields?: unknown[] })?.fields ||
      (currentMeta as { fields?: unknown[] })?.fields
    if (!Array.isArray(fields) || fields.length === 0) return

    if (applySearchColumnFilterFromNavigation(pending)) {
      pendingSearchColumnFilterRef.current = null
      navigate(
        { pathname: location.pathname, search: location.search },
        {
          replace: true,
          state: navigationState?.highlightUid
            ? { highlightUid: navigationState.highlightUid }
            : undefined,
        }
      )
    }
  }, [
    meta,
    currentMeta,
    applySearchColumnFilterFromNavigation,
    navigate,
    location.pathname,
    location.search,
    navigationState?.highlightUid,
  ])

  const clearSearchHighlight = useCallback(() => {
    highlightPageResolveKeyRef.current = ""
    setHighlightUid(null)
    if (navigationState?.highlightUid) {
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: undefined }
      )
    }
  }, [
    navigate,
    location.pathname,
    location.search,
    navigationState?.highlightUid,
  ])

  const handleTablePageChange = useCallback(
    (nextPage: number) => {
      clearSearchHighlight()
      goToPage(nextPage)
    },
    [clearSearchHighlight, goToPage]
  )

  useEffect(() => {
    if (!highlightUid || !currentSlug || !userUidForFetch) {
      return
    }
    if (pendingSearchColumnFilterRef.current) {
      return
    }
    if (Array.isArray(table?.data) && table.data.some((row: any) => row?.uid === highlightUid)) {
      return
    }

    const sort = buildTableSortParams(tableSorting)
    const resolveKey = JSON.stringify({
      highlightUid,
      currentSlug,
      pageSize,
      sort: sort ?? null,
      filters: normalizedCsvFilters,
      columnFilters: serverColumnFilters ?? null,
      archived: archivedListMode,
      organization: routeOrgNorm || "",
    })
    if (highlightPageResolveKeyRef.current === resolveKey) {
      return
    }

    let cancelled = false
    void (async () => {
      const targetPage = await resolveItemListPage(currentSlug, highlightUid, {
        limit: pageSize,
        orderBy: sort?.orderBy,
        orderDirection: sort?.orderDirection,
        filters: normalizedCsvFilters,
        columnFilters: serverColumnFilters,
        archived: archivedListMode,
        organization: routeOrgNorm || undefined,
      })
      if (cancelled || targetPage == null) {
        return
      }
      highlightPageResolveKeyRef.current = resolveKey
      if (targetPage !== page) {
        setPage(targetPage)
        setUpdateList(true)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    highlightUid,
    currentSlug,
    userUidForFetch,
    table?.data,
    page,
    pageSize,
    tableSorting,
    normalizedCsvFilters,
    serverColumnFilters,
    archivedListMode,
    routeOrgNorm,
  ])

  useEffect(() => {
    if (tableError) {
      setHasError(true)
      const errorMsg = formatErrorForDisplay(tableError)
      setErrorMessage(errorMsg)
    } else {
      setHasError(false)
      setErrorMessage(null)
    }
  }, [tableError])

  // Check if this is a 404 error for a mongo collection
  const is404MongoError = tableError?.type === 'COLLECTION_NOT_FOUND_404' && tableError?.isMongoCollection === true
  const isSlugAccessError =
    tableError?.type === 'COMPONENT_NOT_FOUND' || tableError?.type === 'COLLECTION_SLUG_AMBIGUOUS'
  const handleOnEdit = (item?: any) => {
    const targetItem = item ?? currentItem
    const permissions = getItemPermissionFlags(targetItem?.uid)
    if (isItemPermissionPending("update") || !permissions.canUpdate) {
      return
    }
    if (targetItem && isItemArchived(targetItem)) {
      return
    }
    if (typeof item !== 'undefined') {
      const collection = getResolvedCollection();

      dispatch({
        type: ActionTypes.EDIT_ITEM_START,
        payload: {
          data: item,
          slug: currentSlug,
          collection,
        }
      })
    }
    setShowDetailsDrawer(false)
    setShowEditor(true)
  }

  const handleDuplicate = useCallback((item?: any) => {
    const sourceItem = item ?? currentItem
    if (!sourceItem) return
    if (isItemArchived(sourceItem)) return

    const permissions = getItemPermissionFlags(sourceItem?.uid)
    if (isItemPermissionPending("update") || !permissions.canUpdate) {
      return
    }

    const collection = getResolvedCollection()
    const duplicateDraft = { ...sourceItem }
    delete duplicateDraft.uid
    delete duplicateDraft._id
    delete duplicateDraft.objectId
    delete duplicateDraft.createdAt
    delete duplicateDraft.updatedAt
    delete duplicateDraft._created
    delete duplicateDraft._updated
    delete duplicateDraft._creator
    delete duplicateDraft._itemOwner

    dispatch({
      type: ActionTypes.EDIT_ITEM_NEW,
      payload: {
        initial: duplicateDraft,
        slug: currentSlug || currentMeta?.slug,
        fields: currentMeta?.fields || meta?.fields || [],
        collection,
        user: _user,
      },
    })
    setShowDetailsDrawer(false)
    setShowEditor(true)
  }, [
    currentItem,
    isItemPermissionPending,
    getItemPermissionFlags,
    getResolvedCollection,
    dispatch,
    currentSlug,
    currentMeta?.slug,
    currentMeta?.fields,
    meta?.fields,
    _user,
  ])

  const handleImageChange = (item: any, field: any, picture: any) => {
    const collection = getResolvedCollection();
    dispatch({
      type: ActionTypes.EDIT_ITEM_START,
      payload: { data: item, slug: currentSlug, collection }
    });
    const relation = {
      table: collection,
      uid: item.uid,
      field: field.name
    }
    dispatch({ type: ActionTypes.EDIT_ITEM_UPLOAD, payload: { key: field.name, value: picture.filename, item: item, userId: user?.info?.uid || user?.uid, relation: relation, file: picture } })
    dispatch({
      type: ActionTypes.EDIT_ITEM_UPDATE_AND_STORE,
      payload: {
        key: field.name,
        value: picture.filename,
        item: item,
        slug: currentSlug,
        collectionName: collection,
        file: picture,
        listFilters: normalizedCsvFilters,
      }
    })
  }

  const handleFileChange = (item: any, field: any, data: { filename: string }) => {
    const collection = getResolvedCollection();
    dispatch({
      type: ActionTypes.EDIT_ITEM_START,
      payload: { data: item, slug: currentSlug, collection }
    });
    const relation = {
      table: collection,
      uid: item.uid,
      field: field.name
    };
    dispatch({
      type: ActionTypes.FILE_NEW_WITH_RELATION,
      payload: {
        filename: data.filename,
        relation,
      }
    });
    dispatch({
      type: ActionTypes.EDIT_ITEM_UPDATE_AND_STORE,
      payload: {
        key: field.name,
        value: data.filename,
        item,
        slug: currentSlug,
        collectionName: collection,
        listFilters: normalizedCsvFilters,
      }
    });
  }

  const handleCheckboxClick = (item: any, field: any) => {
    const collection = getResolvedCollection();
    dispatch({
      type: ActionTypes.EDIT_ITEM_START,
      payload: { data: item, slug: currentSlug, collection }
    });
    dispatch({
      type: ActionTypes.EDIT_ITEM_UPDATE_AND_STORE,
      payload: {
        key: field.name,
        value: item[field.name] === true ? false : true,
        item,
        slug: currentSlug,
        collectionName: collection,
        listFilters: normalizedCsvFilters,
      }
    })
  }

  const handleRunUserAction = useCallback(
    (
      action: ActionDefinition,
      item: any,
      options?: { skipEditStart?: boolean; skipItemReduxPatch?: boolean; suppressRowSpinner?: boolean }
    ) => {
      const permissions = getItemPermissionFlags(item?.uid)
      if (isItemPermissionPending("update") || !permissions.canUpdate) {
        return
      }
      const operation = action?.operation
      if (!operation?.field) {
        return
      }

      const fieldKey = resolveItemFieldKey(item, String(operation.field).trim())
      const currentValue = item?.[fieldKey]
      let nextValue = currentValue

      const fieldsForAction = (currentMeta?.fields || meta?.fields || []) as any[]
      const fieldDefForAction = fieldsForAction.find((f) => {
        const n = typeof f?.name === "string" ? f.name : ""
        return n.toLowerCase() === String(operation.field).trim().toLowerCase()
      })

      if (operation.type === 'increment' || operation.type === 'decrement') {
        const baseValue = Number(currentValue)
        const amountValue = Number(operation.amount ?? 1)
        const safeBase = Number.isFinite(baseValue) ? baseValue : 0
        const safeAmount = Number.isFinite(amountValue) ? amountValue : 1
        nextValue = operation.type === 'increment' ? safeBase + safeAmount : safeBase - safeAmount
      } else if (operation.type === 'clear') {
        nextValue = resolvedClearedValueForField(fieldDefForAction)
      } else if (operation.type === 'set') {
        nextValue = operation.value
      }

      const collection = getResolvedCollection()
      if (!options?.skipEditStart) {
        dispatch({
          type: ActionTypes.EDIT_ITEM_START,
          payload: {
            data: item,
            slug: currentSlug,
            collection,
          },
        })
      }
      dispatch({
        type: ActionTypes.EDIT_ITEM_UPDATE_AND_STORE,
        payload: {
          key: fieldKey,
          value: nextValue,
          item,
          slug: currentSlug,
          collectionName: collection,
          meta: currentMeta || meta,
          useQuickUpdate: true,
          listFilters: normalizedCsvFilters,
          ...(options?.skipItemReduxPatch ? { skipItemReduxPatch: true } : {}),
        },
      })

      const uid = item?.uid
      if (uid && !options?.suppressRowSpinner) {
        if (actionInProgressTimeoutRef.current) {
          clearTimeout(actionInProgressTimeoutRef.current)
        }
        setActionInProgress({ uid, field: fieldKey })
        actionInProgressTimeoutRef.current = window.setTimeout(() => {
          setActionInProgress(null)
        }, 1200)
      }
    },
    [dispatch, getItemPermissionFlags, getResolvedCollection, isItemPermissionPending, currentSlug, currentMeta, meta, normalizedCsvFilters]
  )

  const applyUserActionToRowTargets = useCallback(
    (action: ActionDefinition, targetsInput: any[]) => {
      const op = action?.operation
      if (!op?.field) {
        setSelectedRows([])
        return
      }
      const targets = targetsInput.filter((r) => r?.uid)
      if (targets.length === 0) {
        setSelectedRows([])
        return
      }
      const multi = targets.length > 1
      const bulkOpts = multi
        ? { skipEditStart: true, skipItemReduxPatch: true, suppressRowSpinner: true }
        : undefined
      const appliedUids: string[] = []
      let spinnerField: string | null = null
      const fieldName = String(op.field).trim()
      for (const row of targets) {
        const perms = getItemPermissionFlags(row.uid)
        if (isItemPermissionPending("update") || !perms.canUpdate) continue
        const fk = resolveItemFieldKey(row, fieldName)
        handleRunUserAction(action, row, bulkOpts)
        appliedUids.push(String(row.uid))
        spinnerField = fk
      }
      if (multi && appliedUids.length > 0 && spinnerField) {
        if (actionInProgressTimeoutRef.current) {
          clearTimeout(actionInProgressTimeoutRef.current)
        }
        setActionInProgress({ uids: appliedUids, field: spinnerField })
        actionInProgressTimeoutRef.current = window.setTimeout(() => {
          setActionInProgress(null)
        }, 1200)
      }
      setSelectedRows([])
    },
    [getItemPermissionFlags, handleRunUserAction, isItemPermissionPending]
  )

  const handleApplyToolbarActionFromMenu = useCallback(
    (item: any) => {
      if (!selectedAction) return
      handleRunUserAction(selectedAction, item)
    },
    [handleRunUserAction, selectedAction]
  )

  const isActionNoop = useCallback((action: ActionDefinition, item: any) => {
    const operation = action?.operation
    if (!operation?.field) {
      return false
    }
    const fieldName = String(operation.field).trim()
    const resolvedKey = resolveItemFieldKey(item, fieldName)
    const currentValue = item?.[resolvedKey]

    if (operation.type === 'clear') {
      const fields = (currentMeta?.fields || meta?.fields || []) as any[]
      const fieldDef = fields.find((f) => {
        const name = typeof f?.name === 'string' ? f.name : ''
        return name.toLowerCase() === fieldName.toLowerCase()
      })
      const target = resolvedClearedValueForField(fieldDef)
      if (target === null) {
        return currentValue === null || currentValue === undefined
      }
      if (Array.isArray(target) && target.length === 0) {
        return Array.isArray(currentValue) && currentValue.length === 0
      }
      if (target === '') {
        return currentValue === '' || currentValue === null || currentValue === undefined
      }
      return currentValue === target
    }

    if (operation.type !== 'set') {
      return false
    }
    const rawActionValue =
      operation.value ??
      (operation as any).amount ??
      action?.name

    const explicitTargetBool = explicitBoolFromOperationValue(operation.value)
    if (explicitTargetBool !== null) {
      const cb = toBoolComparable(currentValue)
      if (cb !== undefined) {
        return cb === explicitTargetBool
      }
      return false
    }

    const fields = (currentMeta?.fields || meta?.fields || []) as any[]
    const fieldDef = fields.find(field => {
      const name = typeof field?.name === 'string' ? field.name : ''
      return name.toLowerCase() === fieldName.toLowerCase()
    })

    const unwrapValue = (value: any) => {
      if (Array.isArray(value) && value.length === 1) {
        return unwrapValue(value[0])
      }
      if (value && typeof value === 'object') {
        if ('value' in value) return (value as any).value
        if ('label' in value) return (value as any).label
        if ('name' in value) return (value as any).name
        if ('title' in value) return (value as any).title
        if ('uid' in value) return (value as any).uid
        if ('id' in value) return (value as any).id
      }
      return value
    }

    const normalizeValue = (value: any) => {
      const unwrapped = unwrapValue(value)
      if (typeof unwrapped === 'string') {
        return unwrapped.trim().toLowerCase()
      }
      return unwrapped
    }

    const normalizedCurrent = normalizeValue(currentValue)
    let normalizedAction = normalizeValue(rawActionValue)

    const fieldTypeLc = String(fieldDef?.type ?? '').toLowerCase()
    if (fieldTypeLc === 'checkbox' || fieldTypeLc === 'boolean') {
      const cb = toBoolComparable(currentValue)
      const ab = toBoolComparable(rawActionValue)
      if (cb !== undefined && ab !== undefined) {
        return cb === ab
      }
    }

    if (fieldDef?.type === 'select' || fieldDef?.type === 'dropdown') {
      const options = fieldDef?.options
      const optionList = Array.isArray(options)
        ? options.map((option: any) => String(option))
        : typeof options === 'string'
          ? options.split(',').map((option: string) => option.trim()).filter(Boolean)
          : []
      if (typeof currentValue === 'number' && optionList[currentValue]) {
        const optionValue = normalizeValue(optionList[currentValue])
        if (optionValue !== undefined) {
          return optionValue === normalizedAction
        }
      }
      if (typeof rawActionValue === 'number' && optionList[rawActionValue]) {
        normalizedAction = normalizeValue(optionList[rawActionValue])
      }
    }

    if (typeof normalizedCurrent === 'number' && typeof normalizedAction === 'string') {
      const parsed = Number(normalizedAction)
      if (Number.isFinite(parsed)) {
        return normalizedCurrent === parsed
      }
    }
    if (typeof normalizedCurrent === 'boolean' && typeof normalizedAction === 'string') {
      if (normalizedAction === 'true' || normalizedAction === 'false') {
        return normalizedCurrent === (normalizedAction === 'true')
      }
    }
    if (typeof normalizedCurrent === 'string' && typeof normalizedAction !== 'string') {
      return normalizedCurrent === String(normalizedAction).trim().toLowerCase()
    }
    return normalizedCurrent === normalizedAction
  }, [currentMeta?.fields, meta?.fields])

  const handleActionRowClick = useCallback((item: any, event: ReactMouseEvent<HTMLElement>) => {
    if (!selectedAction) {
      return
    }
    const permissions = getItemPermissionFlags(item?.uid)
    if (isItemPermissionPending("update") || !permissions.canUpdate) {
      return
    }
    if (isActionNoop(selectedAction, item)) {
      event.preventDefault()
      event.stopPropagation()
      if (actionMenuActions.length === 0) {
        toast.info('This row already matches the selected action.')
        return
      }
      const target = event.currentTarget as HTMLElement | null
      if (target && typeof window !== 'undefined') {
        const rect = target.getBoundingClientRect()
        const clientX = rect.left + rect.width / 2
        const clientY = rect.top + rect.height / 2
        target.dispatchEvent(new MouseEvent('contextmenu', {
          bubbles: true,
          cancelable: true,
          clientX,
          clientY,
          view: window
        }))
      }
      return
    }
    handleRunUserAction(selectedAction, item)
    const uid = item?.uid
    if (uid) {
      if (actionFlashTimeoutRef.current) {
        clearTimeout(actionFlashTimeoutRef.current)
      }
      setActionFlashUid(uid)
      actionFlashTimeoutRef.current = window.setTimeout(() => {
        setActionFlashUid(null)
      }, 1200)
    }
  }, [actionMenuActions.length, getItemPermissionFlags, handleRunUserAction, isActionNoop, isItemPermissionPending, selectedAction])

  const storeEntry = useMemo(() => {
    if (!activeTableKey) {
      return null
    }
    return _tables?.find((t: any) => t?.slug === activeTableKey) || null
  }, [_tables, activeTableKey])

  const storeMetaEntry = useMemo(() => {
    if (!activeTableKey && !routeCollectionSlug) {
      return null
    }
    return (
      _meta?.find(
        (m: any) =>
          m &&
          (m.tableKey === activeTableKey ||
            (m.slug === routeCollectionSlug && normalizeOrgSegment(m.organization) === routeOrgNorm))
      ) || null
    )
  }, [_meta, activeTableKey, routeCollectionSlug, routeOrgNorm])

  const safeStringify = useCallback((value: any) => {
    try {
      return JSON.stringify(value, null, 2)
    } catch (error: any) {
      return JSON.stringify({
        error: 'Unable to serialize payload',
        details: error?.message || String(error),
      }, null, 2)
    }
  }, [])

  const storeJson = useMemo(() => {
    if (!debugMode || !currentSlug) {
      return ''
    }
    const resolved = getResolvedCollection()
    return safeStringify({
      slug: currentSlug,
      collection: resolved,
      table: storeEntry,
      meta: storeMetaEntry,
      error: tableError,
    })
  }, [debugMode, currentSlug, storeEntry, storeMetaEntry, getResolvedCollection, tableError, safeStringify])

  const clientJson = useMemo(() => {
    if (!debugMode || !currentSlug) {
      return ''
    }
    const rows = Array.isArray(table?.data) ? table?.data : []
    return safeStringify({
      slug: currentSlug,
      rowCount: rows.length,
      data: rows,
      selectedRows,
    })
  }, [debugMode, currentSlug, table, selectedRows, safeStringify])

  const reorderableColumnIds = useMemo(() => {
    if (!Array.isArray(columns)) return []
    return (columns as any[])
      .map((col: any) => String(col.id ?? ""))
      .filter((id: string) => id && id !== "select" && id !== "actions")
  }, [columns])

  const tableDataHasFormSubmission = useMemo(() => {
    const rows = Array.isArray(table?.data) ? table.data : []
    return rows.some((row: unknown) => isFormSubmissionRow(row))
  }, [table?.data])

  const defaultColumnOrderIds = useMemo(() => {
    const metaToUse = meta || currentMeta
    if (!metaToUse || !Array.isArray(metaToUse.fields)) {
      const base = [...reorderableColumnIds]
      if (tableDataHasFormSubmission && base.includes("_form")) {
        return ["_form", ...base.filter((id) => id !== "_form")]
      }
      return base
    }
    const fieldOrder = (metaToUse.fields as any[]).map((f: any, index: number) => f?.name || `field_${index}`)
    const ordered = fieldOrder.filter((name: string) => reorderableColumnIds.includes(name))
    const remaining = reorderableColumnIds.filter((id) => !ordered.includes(id))
    let result = [...ordered, ...remaining]
    if (tableDataHasFormSubmission && result.includes("_form")) {
      result = ["_form", ...result.filter((id) => id !== "_form")]
    }
    return result
  }, [meta, currentMeta, reorderableColumnIds, tableDataHasFormSubmission])

  const applyColumnOrder = useCallback((orderIds: string[]) => {
    if (!Array.isArray(columns) || !columns.length) return
    const fixedStartIds = new Set<string>(["select"])
    const fixedEndIds = new Set<string>(["actions"])

    const allCols = columns as any[]
    const fixedStart = allCols.filter((col) => fixedStartIds.has(String(col.id)))
    const fixedEnd = allCols.filter((col) => fixedEndIds.has(String(col.id)))
    const middle = allCols.filter(
      (col) => !fixedStartIds.has(String(col.id)) && !fixedEndIds.has(String(col.id))
    )

    const middleById = new Map<string, any>()
    middle.forEach((col) => {
      const id = String(col.id ?? "")
      if (id) {
        middleById.set(id, col)
      }
    })

    const orderedMiddle: any[] = []
    orderIds.forEach((id) => {
      const col = middleById.get(id)
      if (col) {
        orderedMiddle.push(col)
        middleById.delete(id)
      }
    })

    middleById.forEach((col) => {
      orderedMiddle.push(col)
    })

    setColumns([...fixedStart, ...orderedMiddle, ...fixedEnd])
  }, [columns])

  useEffect(() => {
    if (!currentSlug || !Array.isArray(columns) || !columns.length) {
      return
    }
    if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
      return
    }

    const primaryKey = columnOrderStorageKey
    const legacyKey = `labwick_column_order_${currentSlug || "global"}`

    let stored: string | null = null

    try {
      stored = window.localStorage.getItem(primaryKey)
      if (!stored) {
        stored = window.localStorage.getItem(legacyKey)
      }
    } catch {
      return
    }

    if (!stored) {
      if (Object.values(columnVisibility).some((visible) => visible === false)) {
        setColumnVisibility({})
      }
      return
    }

    try {
      const prefs = parseStoredColumnPrefs(stored)
      if (prefs) {
        let { order: orderIds, hidden } = prefs
        orderIds = [...orderIds]
        const createdIdx = orderIds.indexOf("_created")
        if (createdIdx >= 0) {
          if (!orderIds.includes("_form")) {
            orderIds.splice(orderIds.indexOf("_created"), 0, "_form")
          }
          if (!orderIds.includes("_creator")) {
            orderIds.splice(orderIds.indexOf("_created"), 0, "_creator")
          }
          const createdIdx2 = orderIds.indexOf("_created")
          if (!orderIds.includes("_itemOwner")) {
            orderIds.splice(createdIdx2, 0, "_itemOwner")
          }
        } else {
          if (!orderIds.includes("_form")) orderIds.push("_form")
          if (!orderIds.includes("_creator")) orderIds.push("_creator")
          if (!orderIds.includes("_itemOwner")) orderIds.push("_itemOwner")
        }
        let desiredOrderIds = [
          ...orderIds.filter((id) => reorderableColumnIds.includes(id)),
          ...reorderableColumnIds.filter((id) => !orderIds.includes(id)),
        ]
        if (tableDataHasFormSubmission && desiredOrderIds.includes("_form")) {
          desiredOrderIds = ["_form", ...desiredOrderIds.filter((id) => id !== "_form")]
        }
        if (!areStringArraysEqual(reorderableColumnIds, desiredOrderIds)) {
          applyColumnOrder(desiredOrderIds)
        }
        const nextVisibility = visibilityStateFromHiddenIds(hidden)
        if (!areVisibilityStatesEqual(columnVisibility, nextVisibility)) {
          setColumnVisibility(nextVisibility)
        }
        try {
          window.localStorage.setItem(primaryKey, JSON.stringify({ order: orderIds, hidden }))
        } catch {
          // ignore storage errors
        }
      } else {
        if (Object.values(columnVisibility).some((visible) => visible === false)) {
          setColumnVisibility({})
        }
      }
    } catch {
      if (Object.values(columnVisibility).some((visible) => visible === false)) {
        setColumnVisibility({})
      }
    }
  }, [
    currentSlug,
    columns,
    applyColumnOrder,
    reorderableColumnIds,
    columnOrderStorageKey,
    columnVisibility,
    columnPrefsRevision,
    tableDataHasFormSubmission,
  ])

  useEffect(() => {
    if (!userIdForColumnPrefs || !columnViewTableKey) return
    if (typeof window === "undefined" || typeof window.localStorage === "undefined") return

    const writeLocal = (view: ColumnViewPref) => {
      const next = JSON.stringify({ order: view.order, hidden: view.hidden })
      try {
        if (window.localStorage.getItem(columnOrderStorageKey) !== next) {
          window.localStorage.setItem(columnOrderStorageKey, next)
          setColumnPrefsRevision((revision) => revision + 1)
        }
      } catch {
        // ignore storage errors
      }
    }

    const sessionView = readSessionColumnView(userIdForColumnPrefs, columnViewTableKey)
    const legacyKey = `labwick_column_order_${columnViewTableKey}`
    if (sessionView === null) {
      try {
        const hadStored = Boolean(
          window.localStorage.getItem(columnOrderStorageKey) || window.localStorage.getItem(legacyKey)
        )
        window.localStorage.removeItem(columnOrderStorageKey)
        window.localStorage.removeItem(legacyKey)
        if (hadStored) {
          setColumnPrefsRevision((revision) => revision + 1)
        }
      } catch {
        // ignore storage errors
      }
      return
    }
    if (sessionView) {
      writeLocal(sessionView)
      return
    }

    if (!Array.isArray(user?.columnViews)) return

    const serverView = findColumnView(user.columnViews, columnViewTableKey)
    if (serverView) {
      writeLocal(serverView)
      return
    }

    let stored: string | null = null
    try {
      stored = window.localStorage.getItem(columnOrderStorageKey) || window.localStorage.getItem(legacyKey)
    } catch {
      return
    }
    const localView = stored ? parseStoredColumnPrefs(stored) : null
    if (!localView) return

    const shipKey = `${userIdForColumnPrefs}:${columnViewTableKey}`
    if (shippedColumnViewsRef.current.has(shipKey)) return
    shippedColumnViewsRef.current.add(shipKey)
    void authAPI.saveColumnView({
      tableKey: columnViewTableKey,
      order: localView.order,
      hidden: localView.hidden,
    }).catch((error) => {
      shippedColumnViewsRef.current.delete(shipKey)
      console.error("Failed to save column layout", error)
    })
  }, [columnOrderStorageKey, columnViewTableKey, user?.columnViews, userIdForColumnPrefs])

  useEffect(() => {
    if (!tableDataHasFormSubmission || !reorderableColumnIds.includes("_form")) {
      return
    }
    if (reorderableColumnIds[0] === "_form") {
      return
    }
    const desired = ["_form", ...reorderableColumnIds.filter((id) => id !== "_form")]
    applyColumnOrder(desired)
  }, [tableDataHasFormSubmission, reorderableColumnIds, applyColumnOrder])

  // Make sure we have a cleanup effect for any lingering dialog issues
  useEffect(() => {
    return () => {
      // Cleanup function to clear any timers when component unmounts
      clearTimeout((window as any).deleteTimeout);
      clearTimeout((window as any).refreshTimeout);
      if (actionFlashTimeoutRef.current) {
        clearTimeout(actionFlashTimeoutRef.current);
      }
    };
  }, []);

  const currentColumnOrderDraft = useMemo(() => {
    if (columnOrderDraft && columnOrderDraft.length > 0) {
      return columnOrderDraft
    }
    return defaultColumnOrderIds
  }, [columnOrderDraft, defaultColumnOrderIds])

  const fieldLabelByName = useMemo(() => {
    const metaToUse = meta || currentMeta
    const out: Record<string, string> = {}
    if (metaToUse && Array.isArray((metaToUse as { fields?: unknown[] }).fields)) {
      ; ((metaToUse as { fields: { name?: string; label?: string }[] }).fields).forEach((f) => {
        const name = f?.name
        if (name) out[name] = f.label || f.name || name
      })
    }
    out._form = "Form"
    out._creator = "Creator"
    out._itemOwner = "Item owner"
    return out
  }, [meta, currentMeta])

  const columnOrderVisibleCount = useMemo(() => {
    if (columnHiddenDraft === null) {
      return currentColumnOrderDraft.length
    }
    const hidden = new Set(columnHiddenDraft)
    return currentColumnOrderDraft.filter((id) => !hidden.has(id)).length
  }, [columnHiddenDraft, currentColumnOrderDraft])

  const handleColumnOrderDialogOpenChange = useCallback((open: boolean) => {
    setColumnOrderDialogOpen(open)
    if (!open) {
      setColumnHiddenDraft(null)
    }
  }, [])

  const toggleColumnHiddenInDraft = useCallback((id: string) => {
    setColumnHiddenDraft((prev) => {
      const list = prev ?? []
      const next = new Set(list)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return [...next]
    })
  }, [])

  const moveColumnInDraft = useCallback((id: string, direction: "up" | "down") => {
    setColumnOrderDraft((prev) => {
      const base = prev && prev.length === reorderableColumnIds.length ? prev : defaultColumnOrderIds
      const list = [...base]
      const index = list.indexOf(id)
      if (index === -1) return list
      const targetIndex = direction === "up" ? index - 1 : index + 1
      if (targetIndex < 0 || targetIndex >= list.length) return list
      const tmp = list[targetIndex]
      list[targetIndex] = list[index]
      list[index] = tmp
      return list
    })
  }, [defaultColumnOrderIds, reorderableColumnIds.length])

  const handleColumnDrop = useCallback((sourceId: string, targetId: string) => {
    if (!sourceId || !targetId || sourceId === targetId) return
    setColumnOrderDraft((prev) => {
      const base = prev && prev.length === reorderableColumnIds.length ? prev : defaultColumnOrderIds
      const list = [...base]
      const fromIndex = list.indexOf(sourceId)
      const toIndex = list.indexOf(targetId)
      if (fromIndex === -1 || toIndex === -1) return list
      list.splice(fromIndex, 1)
      list.splice(toIndex, 0, sourceId)
      return list
    })
  }, [defaultColumnOrderIds, reorderableColumnIds.length])

  const handleOpenColumnSettings = useCallback(() => {
    setColumnOrderDraft(reorderableColumnIds)
    setColumnHiddenDraft(reorderableColumnIds.filter((id) => columnVisibility[id] === false))
    setColumnOrderDialogOpen(true)
  }, [reorderableColumnIds, columnVisibility])

  const handleSaveColumnOrder = useCallback(() => {
    if (!currentSlug) {
      setColumnOrderDialogOpen(false)
      setColumnHiddenDraft(null)
      return
    }
    const finalOrder = currentColumnOrderDraft
    const hiddenList = columnHiddenDraft ?? []
    const hiddenSet = new Set(hiddenList)
    const visibleCount = finalOrder.filter((id) => !hiddenSet.has(id)).length
    if (visibleCount === 0) {
      toast.error("Keep at least one column visible")
      return
    }
    if (Array.isArray(finalOrder) && finalOrder.length > 0) {
      applyColumnOrder(finalOrder)
      setColumnVisibility(visibilityStateFromHiddenIds(hiddenList))
      const savedView = { order: finalOrder, hidden: hiddenList }
      if (typeof window !== "undefined" && typeof window.localStorage !== "undefined") {
        const key = columnOrderStorageKey
        try {
          window.localStorage.setItem(key, JSON.stringify(savedView))
        } catch {
          // ignore storage errors
        }
      }
      if (userIdForColumnPrefs && columnViewTableKey) {
        rememberSessionColumnView(userIdForColumnPrefs, columnViewTableKey, savedView)
        shippedColumnViewsRef.current.add(`${userIdForColumnPrefs}:${columnViewTableKey}`)
        void authAPI.saveColumnView({
          tableKey: columnViewTableKey,
          order: savedView.order,
          hidden: savedView.hidden,
        }).catch((error) => {
          console.error("Failed to save column layout", error)
          toast.error("Could not save column layout to your account")
        })
      }
    }
    setColumnOrderDialogOpen(false)
    setColumnHiddenDraft(null)
  }, [
    applyColumnOrder,
    columnHiddenDraft,
    currentColumnOrderDraft,
    currentSlug,
    columnOrderStorageKey,
    columnViewTableKey,
    userIdForColumnPrefs,
  ])

  const handleResetColumnOrder = useCallback(() => {
    if (!currentSlug) {
      setColumnOrderDraft(null)
      setColumnHiddenDraft(null)
      return
    }
    if (typeof window !== "undefined" && typeof window.localStorage !== "undefined") {
      try {
        window.localStorage.removeItem(columnOrderStorageKey)
        if (columnViewTableKey) {
          window.localStorage.removeItem(`labwick_column_order_${columnViewTableKey}`)
        }
      } catch {
        // ignore storage errors
      }
    }
    if (userIdForColumnPrefs && columnViewTableKey) {
      rememberSessionColumnView(userIdForColumnPrefs, columnViewTableKey, null)
      shippedColumnViewsRef.current.add(`${userIdForColumnPrefs}:${columnViewTableKey}`)
      void authAPI.saveColumnView({
        tableKey: columnViewTableKey,
        clear: true,
      }).catch((error) => {
        console.error("Failed to save column layout", error)
        toast.error("Could not save column layout to your account")
      })
    }
    setColumnOrderDraft(null)
    setColumnHiddenDraft(null)
    setColumnVisibility({})
    applyColumnOrder(defaultColumnOrderIds)
  }, [applyColumnOrder, currentSlug, defaultColumnOrderIds, columnOrderStorageKey, columnViewTableKey, userIdForColumnPrefs])

  const handleDeleteClick = (item: any) => {
    const permissions = getItemPermissionFlags(item?.uid)
    const deleteAllowed = selectedRows.length > 1 ? canDeleteSelected : permissions.canDelete
    if (isItemPermissionPending("delete") || !deleteAllowed) {
      return
    }
    const collection = getResolvedCollection();

    // Use the first item as current item for confirmation dialog
    dispatch({
      type: ActionTypes.EDIT_ITEM_START,
      payload: {
        data: item,
        slug: currentSlug,
        collection
      }
    });

    // Ensure state update completes before showing dialog
    setTimeout(() => {
      setOpenDeleteDialog(true);
    }, 0);
  }

  const isArchiveMenuDisabledForRow = useCallback(
    (row: any) => {
      if (selectedRows.length > 0) {
        return !canArchiveSelected
      }
      if (!row?.uid) {
        return true
      }
      if (isItemArchived(row)) {
        return true
      }
      if (isItemPermissionPending("update")) {
        return true
      }
      return !getItemPermissionFlags(row.uid).canUpdate
    },
    [
      selectedRows.length,
      canArchiveSelected,
      isItemPermissionPending,
      getItemPermissionFlags,
    ]
  )

  const isRestoreMenuDisabledForRow = useCallback(
    (row: any) => {
      if (selectedRows.length > 0) {
        return !canRestoreSelected
      }
      if (!row?.uid) {
        return true
      }
      if (!isItemArchived(row)) {
        return true
      }
      if (isItemPermissionPending("update")) {
        return true
      }
      return !getItemPermissionFlags(row.uid).canUpdate
    },
    [
      selectedRows.length,
      canRestoreSelected,
      isItemPermissionPending,
      getItemPermissionFlags,
    ]
  )

  const handleArchiveClick = useCallback(
    async (explicitItem?: any) => {
      const slugToUse = (currentSlug || currentMeta?.slug || "").trim()
      if (!slugToUse) {
        toast.error("No collection selected")
        return
      }
      const org =
        (currentMeta?.organization || routeOrgSegment || "").trim() || undefined
      const items: any[] =
        selectedRows.length > 0
          ? selectedRows.filter((r: any) => r?.uid)
          : explicitItem?.uid
            ? [explicitItem]
            : []
      if (items.length === 0) {
        return
      }

      if (isItemPermissionPending("update")) {
        toast.error("Still loading permissions")
        return
      }
      for (const row of items) {
        if (isItemArchived(row)) {
          toast.error("One or more items are already archived")
          return
        }
        if (!getItemPermissionFlags(row.uid).canUpdate) {
          toast.error("You do not have permission to archive one or more selected items")
          return
        }
      }

      const msg =
        items.length > 1
          ? `Archive ${items.length} items? They will be hidden from the default list.`
          : "Archive this item? It will be hidden from the default list."
      if (typeof window !== "undefined" && !window.confirm(msg)) {
        return
      }

      try {
        const uids = items.map((row) => row.uid).filter(Boolean) as string[]
        for (const row of items) {
          await archiveItemAPI(slugToUse, row.uid, org ?? null)
        }
        dispatch({
          type: ActionTypes.PATCH_TABLE_ITEMS_ARCHIVED,
          payload: { uids, archived: true, listMode: archivedListMode },
        })
        toast.success(items.length > 1 ? `${items.length} items archived` : "Item archived")
        setSelectedRows([])
        refreshList()
      } catch (err) {
        toast.error(formatErrorForDisplay(err))
      }
    },
    [
      selectedRows,
      currentSlug,
      currentMeta,
      routeOrgSegment,
      routeOrgNorm,
      isItemPermissionPending,
      getItemPermissionFlags,
      user,
      activeTableKey,
      normalizedCsvFilters,
      archivedListMode,
      dispatch,
      refreshList,
    ]
  )

  const handleRestoreClick = useCallback(
    async (explicitItem?: any) => {
      const slugToUse = (currentSlug || currentMeta?.slug || "").trim()
      if (!slugToUse) {
        toast.error("No collection selected")
        return
      }
      const org =
        (currentMeta?.organization || routeOrgSegment || "").trim() || undefined
      const items: any[] =
        selectedRows.length > 0
          ? selectedRows.filter((r: any) => r?.uid)
          : explicitItem?.uid
            ? [explicitItem]
            : []
      if (items.length === 0) {
        return
      }

      if (isItemPermissionPending("update")) {
        toast.error("Still loading permissions")
        return
      }
      for (const row of items) {
        if (!isItemArchived(row)) {
          toast.error("One or more items are not archived")
          return
        }
        if (!getItemPermissionFlags(row.uid).canUpdate) {
          toast.error("You do not have permission to restore one or more selected items")
          return
        }
      }

      const msg =
        items.length > 1
          ? `Restore ${items.length} items?`
          : "Restore this item?"
      if (typeof window !== "undefined" && !window.confirm(msg)) {
        return
      }

      try {
        const uids = items.map((row) => row.uid).filter(Boolean) as string[]
        for (const row of items) {
          await restoreItemAPI(slugToUse, row.uid, org ?? null)
        }
        dispatch({
          type: ActionTypes.PATCH_TABLE_ITEMS_ARCHIVED,
          payload: { uids, archived: false, listMode: archivedListMode },
        })
        toast.success(items.length > 1 ? `${items.length} items restored` : "Item restored")
        setSelectedRows([])
        refreshList()
      } catch (err) {
        toast.error(formatErrorForDisplay(err))
      }
    },
    [
      selectedRows,
      currentSlug,
      currentMeta,
      routeOrgSegment,
      routeOrgNorm,
      isItemPermissionPending,
      getItemPermissionFlags,
      user,
      activeTableKey,
      normalizedCsvFilters,
      archivedListMode,
      dispatch,
      refreshList,
    ]
  )

  const handelShowDetailsClick = (item: any) => {
    const permissions = getItemPermissionFlags(item?.uid)
    if (isItemPermissionPending("read") || !permissions.canRead) {
      return
    }
    const collection = getResolvedCollection();

    dispatch({
      type: ActionTypes.EDIT_ITEM_START,
      payload: {
        data: item,
        slug: currentSlug,
        collection
      }
    });
    setShowDetailsDrawer(true)
  }

  const openActivityLogEntryItem = useCallback(
    async (entry: ActivityLogEntry) => {
      const itemId = entry.itemId?.trim()
      if (!itemId || !currentSlug) return
      const permissions = getItemPermissionFlags(itemId)
      if (isItemPermissionPending("read") || !permissions.canRead) {
        return
      }
      const collection = getResolvedCollection()
      const org = currentMeta?.organization || routeOrgNorm || undefined
      let item: any = Array.isArray(table?.data)
        ? table.data.find((r: any) => r?.uid === itemId)
        : undefined
      if (!item) {
        try {
          const res = await dataAPI.getCollectionItem(currentSlug, itemId, org ?? null)
          item = res?.data
          if (!item || typeof item !== "object") return
        } catch {
          return
        }
      }
      dispatch({
        type: ActionTypes.EDIT_ITEM_START,
        payload: {
          data: item,
          slug: currentSlug,
          collection,
        },
      })
      setShowDetailsDrawer(true)
    },
    [
      currentSlug,
      currentMeta?.organization,
      routeOrgNorm,
      dispatch,
      getItemPermissionFlags,
      getResolvedCollection,
      isItemPermissionPending,
      table?.data,
    ]
  )

  const handleUserInfoDialogOpenChange = useCallback((open: boolean) => {
    setUserInfoDialogOpen(open)
    if (!open) {
      setUserInfoDialogUid(null)
      setUserInfoDialogProfile(null)
      setUserInfoDialogPreview(null)
      setUserInfoDialogLoading(false)
    }
  }, [])

  const handleShowUser = useCallback((userId: string) => {
    const uid = String(userId ?? "").trim()
    if (!uid) return
    setUserInfoDialogUid(uid)
    setUserInfoDialogProfile(null)
    setUserInfoDialogPreview(getUserUidDisplayNameAndEmail(uid))
    setUserInfoDialogLoading(true)
    setUserInfoDialogOpen(true)
    void ensureUserUidCachedFromAuth(uid).then(() => {
      setUserInfoDialogPreview(getUserUidDisplayNameAndEmail(uid))
    })
    void authAPI
      .getUserById(uid)
      .then((u) => {
        setUserInfoDialogProfile(u)
      })
      .finally(() => {
        setUserInfoDialogLoading(false)
      })
  }, [])

  const handleShowUserInTable = useCallback(() => {
    if (!userInfoDialogUid) return
    navigate(`/admin/users?uid=${encodeURIComponent(userInfoDialogUid)}`)
    handleUserInfoDialogOpenChange(false)
  }, [navigate, userInfoDialogUid, handleUserInfoDialogOpenChange])

  const handleFormSubmissionShowUser = useCallback((row: Record<string, unknown>) => {
    setFormSubmissionUserDoc(row)
  }, [])

  const handleFormSubmissionShowForm = useCallback(async (row: Record<string, unknown>) => {
    const uid = String(row._formUid ?? "").trim()
    if (!uid) {
      return
    }
    try {
      const cfg = await fetchFormConfig(uid)
      const token = cfg.publicToken?.trim()
      if (!token) {
        toast.error("This form has no public token")
        return
      }
      window.open(`/forms/${encodeURIComponent(token)}`, "_blank", "noopener,noreferrer")
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to load form"
      toast.error(message)
    }
  }, [])

  const handleFormSubmissionShowLogs = useCallback(() => {
    navigate("/admin/logs")
  }, [navigate])

  const handleFormSubmissionImpersonateUser = useCallback(async (row: Record<string, unknown>) => {
    const uid = resolveItemCreatorUid(row)
    if (!uid) {
      toast.error("No submitter user ID on this submission")
      return
    }
    if (uidsEqual(uid, authUser?.uid)) {
      toast.error("You are already logged in as this user")
      return
    }
    setImpersonatingSubmissionUid(uid)
    try {
      await impersonateUserByUid(uid)
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to impersonate user"
      toast.error(message)
      setImpersonatingSubmissionUid(null)
    }
  }, [authUser?.uid])

  const mongoInspectorCollection = useMemo(() => {
    const merged = meta && currentMeta ? { ...currentMeta, ...meta } : meta || currentMeta
    return mongoCollectionNameForInspector(merged)
  }, [meta, currentMeta])
  const isMongoDatasource = (meta || currentMeta)?.dataSource === "mongodb"

  const handleShowInMongoFromContext = useCallback(
    (item: { uid?: string }) => {
      const name = mongoInspectorCollection
      const uid = item?.uid != null ? String(item.uid).trim() : ""
      if (!name || !uid) {
        return
      }
      navigate(buildAdminMongoDocumentUrl(name, uid))
    },
    [mongoInspectorCollection, navigate]
  )

  const exportableFieldsFromMeta = useMemo(() => {
    const metaToUse = meta || currentMeta
    const fromMeta = metaToUse?.fields
      ? (metaToUse.fields as any[]).map((f: any, i: number) => ({
        name: f.name || `field_${i}`,
        label: f.label || f.name || `field_${i}`,
        order: f.order ?? i
      }))
      : []
    const withOrder = [...fromMeta].sort((a, b) => a.order - b.order)
    return [
      ...withOrder,
      { name: '_form', label: 'Form', order: 99995 },
      { name: '_creator', label: 'Creator', order: 99996 },
      { name: '_itemOwner', label: 'Item owner', order: 99997 },
      { name: '_created', label: 'Created', order: 99998 },
      { name: '_updated', label: 'Updated', order: 99999 }
    ]
  }, [meta, currentMeta])

  const collectionHasRelationFields = useMemo(() => {
    const fields = (meta || currentMeta)?.fields as any[] | undefined
    if (!fields?.length) return false
    return fields.some(
      (f: any) =>
        f?.type === 'relation' || f?.type === 'multiplerelation' || f?.type === 'systemrelation'
    )
  }, [meta, currentMeta])

  const stringifyCellForExport = useCallback((value: any): string => {
    if (value == null) return ''
    if (value && typeof value === 'object' && value.seconds !== undefined) {
      const d = new Date(value.seconds * 1000)
      return isNaN(d.getTime()) ? '' : d.toISOString()
    }
    if (value instanceof Date) return isNaN(value.getTime()) ? '' : value.toISOString()
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }, [])

  const getExportRows = useCallback((scope: 'all' | 'selection') => {
    const data = Array.isArray(table?.data) ? table?.data : []
    if (scope === 'selection') return selectedRows.length ? selectedRows : []
    return data
  }, [table?.data, selectedRows])

  const runExport = useCallback(async (
    scope: 'all' | 'selection',
    format: 'json' | 'xlsx' | 'txt' | 'semicolon' | 'csv',
    fieldNames: string[],
    relationMode: 'labels' | 'ids'
  ) => {
    let rows: Record<string, unknown>[]
    if (scope === 'all') {
      const slugForExport = (_currentSlug || currentSlug || '').trim()
      if (!slugForExport) return
      rows = await fetchAllCollectionItemsForExport({
        slug: slugForExport,
        organization: routeOrgNorm || undefined,
        filters: normalizedCsvFilters,
        columnFilters: serverColumnFilters,
        archived: archivedListMode,
        sorting: tableSorting,
      })
    } else {
      rows = getExportRows(scope) as Record<string, unknown>[]
    }
    if (!rows.length) return
    const metaToUse = meta || currentMeta
    const fields = (metaToUse?.fields as any[]) || []
    const nameToLabel: Record<string, string> = {}
    const nameToFieldMeta: Record<string, any> = {}
    fields.forEach((f: any, i: number) => {
      const name = f.name || `field_${i}`
      nameToLabel[name] = f.label || f.name || name
      nameToFieldMeta[name] = f
    })
    nameToLabel['_form'] = 'Form'
    nameToLabel['_creator'] = 'Creator'
    nameToLabel['_itemOwner'] = 'Item owner'
    nameToLabel['_created'] = 'Created'
    nameToLabel['_updated'] = 'Updated'
    nameToFieldMeta['_form'] = {}
    nameToFieldMeta['_creator'] = {}
    nameToFieldMeta['_itemOwner'] = {}
    nameToFieldMeta['_created'] = {}
    nameToFieldMeta['_updated'] = {}

    const isRelationType = (t: string) =>
      t === 'relation' || t === 'multiplerelation' || t === 'systemrelation'

    const includeItemUidColumn =
      relationMode === 'ids' || !collectionHasRelationFields
    const effectiveFieldNames =
      includeItemUidColumn && !fieldNames.includes('uid')
        ? ['uid', ...fieldNames]
        : fieldNames
    if (includeItemUidColumn && !fieldNames.includes('uid')) {
      nameToLabel['uid'] = 'uid'
      nameToFieldMeta['uid'] = { type: 'text', name: 'uid' }
    }

    const relationSlugsSeen = new Set<string>()
    if (effectiveFieldNames.some((k) => k === '_creator' || k === '_itemOwner')) {
      await preloadUsersRelationLabels().catch(() => undefined)
      const uidSet = new Set<string>()
      for (const row of rows) {
        if (effectiveFieldNames.includes('_creator')) {
          const u = resolveItemCreatorUid(row as Record<string, unknown>)
          if (u) uidSet.add(String(u).trim())
        }
        if (effectiveFieldNames.includes('_itemOwner')) {
          const u = resolveItemOwnerUid(row as Record<string, unknown>)
          if (u) uidSet.add(String(u).trim())
        }
      }
      await hydrateUserDisplayCacheFromAuthForUids(uidSet)
    }
    if (relationMode === 'labels') {
      for (const k of effectiveFieldNames) {
        const fieldMeta = nameToFieldMeta[k]
        if (fieldMeta && isRelationType(fieldMeta.type) && fieldMeta.value && !relationSlugsSeen.has(fieldMeta.value)) {
          relationSlugsSeen.add(fieldMeta.value)
          await ensureRelationLabelsLoaded(fieldMeta.value, fieldMeta.fields, fieldMeta.type)
        }
      }
    }

    const getRawCellValue = (row: Record<string, unknown>, fieldName: string, fieldMeta: any) => {
      if (fieldName === 'uid') {
        const u = (row as any).uid
        return u != null ? String(u).trim() : ''
      }
      if (fieldName === '_form') {
        const uidRaw = row._formUid
        const uid =
          typeof uidRaw === 'string'
            ? uidRaw.trim()
            : uidRaw != null
              ? String(uidRaw).trim()
              : ''
        const t = row._formTitle
        const fromRow = typeof t === 'string' && t.trim() ? t.trim() : ''
        const fromRegistry = (formTitleByUid[uid] ?? '').trim()
        const title = fromRow || fromRegistry
        if (!uid) return ''
        return title ? `${title} (${uid})` : uid
      }
      if (fieldName === '_creator') return resolveItemCreatorUid(row)
      if (fieldName === '_itemOwner') return resolveItemOwnerUid(row)
      if (fieldName === '_created') return (row as any).createdAt ?? (row as any)._created
      if (fieldName === '_updated') return (row as any).updatedAt ?? (row as any)._updated
      if (fieldMeta?.type === 'systemrelation' && fieldMeta?.sourceField) {
        const v = fieldMeta.sourceField.includes('.')
          ? fieldMeta.sourceField.split('.').reduce((acc: any, key: string) => acc?.[key], row)
          : (row as any)[fieldMeta.sourceField]
        return v !== undefined ? v : (row as any)[fieldName]
      }
      return (row as any)[fieldName]
    }

    const resolveRowForExport = (row: Record<string, unknown>) => {
      const out: Record<string, unknown> = {}
      effectiveFieldNames.forEach(k => {
        const fieldMeta = nameToFieldMeta[k]
        const raw = getRawCellValue(row, k, fieldMeta)
        if (k === '_creator' || k === '_itemOwner') {
          const uid = raw == null ? '' : String(raw).trim()
          if (!uid) {
            out[k] = ''
          } else {
            const entry = getUserUidDisplayNameAndEmail(uid)
            out[k] = entry?.email?.trim() ?? ''
          }
        } else if (fieldMeta && isRelationType(fieldMeta.type)) {
          out[k] =
            relationMode === 'ids' ? relationRawToExportIds(raw, fieldMeta) : getRelationDisplayValue(fieldMeta, raw)
        } else {
          out[k] = raw
        }
      })
      return out
    }

    const dataForExport = rows.map((row: Record<string, unknown>) => resolveRowForExport(row))
    const baseName = `${displayName.replace(/\s+/g, '-')}-export`

    if (format === 'json') {
      const blob = new Blob([JSON.stringify(dataForExport, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${baseName}.json`
      a.click()
      URL.revokeObjectURL(url)
      return
    }

    if (format === 'txt') {
      const headers = effectiveFieldNames.map(k => nameToLabel[k] || k)
      const lines = [headers.join('\t'), ...dataForExport.map((row: Record<string, unknown>) =>
        effectiveFieldNames.map(k => stringifyCellForExport(row[k])).join('\t')
      )]
      const blob = new Blob([lines.join('\n')], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${baseName}.txt`
      a.click()
      URL.revokeObjectURL(url)
      return
    }

    if (format === 'csv') {
      const headers: string[] = effectiveFieldNames.map(k => nameToLabel[k] || k)
      const rowValues: string[][] = dataForExport.map((row: Record<string, unknown>) =>
        effectiveFieldNames.map(k => stringifyCellForExport(row[k]))
      )

      const csvLines = [headers, ...rowValues].map((cells: string[]) => joinCsvRow(cells))

      const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${baseName}.csv`
      a.click()
      URL.revokeObjectURL(url)
      return
    }

    if (format === 'semicolon') {
      const key = fieldNames[0]
      if (!key) return
      const line = dataForExport.map((row: Record<string, unknown>) => stringifyCellForExport(row[key])).join(';')
      const blob = new Blob([line], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${baseName}-semicolon.txt`
      a.click()
      URL.revokeObjectURL(url)
      return
    }

    if (format === 'xlsx') {
      const workbook = new ExcelJS.Workbook()
      const sheetName = displayName.slice(0, 31)
      const worksheet = workbook.addWorksheet(sheetName)
      const rowData = [
        effectiveFieldNames.map(k => nameToLabel[k] || k),
        ...dataForExport.map((row: Record<string, unknown>) => effectiveFieldNames.map(k => stringifyCellForExport(row[k])))
      ]
      rowData.forEach(row => worksheet.addRow(row))
      workbook.xlsx.writeBuffer().then((buffer: ArrayBuffer) => {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${baseName}.xlsx`
        a.click()
        URL.revokeObjectURL(url)
      })
    }
  }, [
    getExportRows,
    _currentSlug,
    currentSlug,
    routeOrgNorm,
    normalizedCsvFilters,
    serverColumnFilters,
    archivedListMode,
    tableSorting,
    meta,
    currentMeta,
    displayName,
    stringifyCellForExport,
    formTitleByUid,
    collectionHasRelationFields,
  ])

  const openExportFieldDialog = useCallback((scope: 'all' | 'selection', format: 'json' | 'xlsx' | 'txt' | 'semicolon' | 'csv') => {
    const rows = getExportRows(scope)
    if (!rows.length && scope === 'selection') return
    setExportParams({ scope, format })
    setExportAllFields(true)
    setExportSelectedFields([])
    setExportRelationMode('labels')
    const first = exportableFieldsFromMeta[0]
    setExportSemicolonField(first?.name ?? '')
    setExportDialogOpen(true)
  }, [getExportRows, exportableFieldsFromMeta])

  const confirmExport = useCallback(async () => {
    if (!exportParams) return
    const { scope, format } = exportParams
    let fieldNames: string[]
    if (format === 'semicolon') {
      fieldNames = exportSemicolonField ? [exportSemicolonField] : []
    } else {
      fieldNames = exportAllFields
        ? exportableFieldsFromMeta.map(f => f.name)
        : exportSelectedFields
    }
    if (!fieldNames.length) return
    setExportLoading(true)
    try {
      await runExport(scope, format, fieldNames, exportRelationMode)
      setExportDialogOpen(false)
      setExportParams(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Export failed"
      toast.error(message)
    } finally {
      setExportLoading(false)
    }
  }, [
    exportParams,
    exportAllFields,
    exportSelectedFields,
    exportSemicolonField,
    exportableFieldsFromMeta,
    exportRelationMode,
    runExport,
  ])

  useComponentTableColumns({
    columnHelper,
    setColumns,
    currentSlug,
    meta,
    currentMeta,
    actionMode,
    debugMode,
    selectedRows,
    setSelectedRows,
    activeFilterColumnId,
    setActiveFilterColumnId,
    csvFilters,
    createdRange,
    setCreatedRange,
    updatedRange,
    setUpdatedRange,
    createdRangeRef,
    updatedRangeRef,
    parseTimestamp: parseTableTimestamp,
    renderFieldFilter,
    isFieldFilterApplied,
    renderCsvButton,
    clearCsvForField,
    handleImageChange,
    handleFileChange,
    handleCheckboxClick,
    getResolvedCollection,
    openRelationDialog,
    getItemPermissionFlags,
    isItemPermissionPending,
    canDeleteSelected,
    canDeleteCollection,
    deletePermissionLoading,
    componentActions,
    handleRunUserAction,
    handleShowUser,
    handleDeleteClick,
    handleArchiveClick,
    isArchiveMenuDisabledForRow,
    handleRestoreClick,
    isRestoreMenuDisabledForRow,
    handelShowDetailsClick,
    handleOnEdit,
    handleDuplicate,
    getItemDelegateActionsVisible,
    openDelegateOwnerDialog,
    requestRemoveDelegatedOwner,
    rowHasDelegatedOwner,
    tableRowsForUserHydration: table?.data,
    selectFilters,
    formTitleByUid,
    isAdmin,
    onFormSubmissionShowUser: handleFormSubmissionShowUser,
    onFormSubmissionShowForm: handleFormSubmissionShowForm,
    onFormSubmissionShowLogs: handleFormSubmissionShowLogs,
    onFormSubmissionImpersonateUser: isAdmin ? handleFormSubmissionImpersonateUser : undefined,
    impersonatingSubmissionUid: isAdmin ? impersonatingSubmissionUid : null,
    currentUserUid: authUser?.uid ?? null,
  })

  // Removed useEffect that was calling refreshList() on every currentItem change
  // This was causing the table to reload on every keystroke in the edit form
  // refreshList() is already called explicitly when needed:
  // - handleOnChange (when item is saved via onEdited callback)
  // - handleImageChange (when image is uploaded)
  // - handleCheckboxClick (when checkbox is toggled)
  // - handleDeleteClick (when item is deleted)

  const handleNewClick = () => {
    // Try to get meta from multiple sources in order of preference
    let metaToUse = meta;

    // Check if meta is valid (not empty array or empty object)
    const isValidMeta = (m: any) => m && !Array.isArray(m) && Object.keys(m).length > 0 && m.fields && m.fields.length > 0;

    // Priority 1: Use meta from error object (for 404 cases where meta was set in error)
    if (!isValidMeta(metaToUse) && tableError?.componentMeta && isValidMeta(tableError.componentMeta)) {
      metaToUse = tableError.componentMeta;
    }

    // Priority 2: Use currentMeta from Redux (most up-to-date)
    if (!isValidMeta(metaToUse) && currentMeta && isValidMeta(currentMeta)) {
      metaToUse = currentMeta;
    }

    // Priority 3: Use _currentMeta directly from Redux selector
    if (!isValidMeta(metaToUse) && _currentMeta && isValidMeta(_currentMeta)) {
      metaToUse = _currentMeta;
    }

    // Priority 4: Try to get from allMeta Redux state
    if (!isValidMeta(metaToUse) && allMeta) {
      const foundMeta = allMeta.find(
        (m: any) =>
          m &&
          isValidMeta(m) &&
          (m.tableKey === activeTableKey ||
            (m.slug === routeCollectionSlug && normalizeOrgSegment(m.organization) === routeOrgNorm))
      );
      if (foundMeta) {
        metaToUse = foundMeta;
      }
    }

    if (!isValidMeta(metaToUse)) {
      console.error('Cannot add item: No fields defined in meta', {
        meta,
        currentMeta,
        _currentMeta,
        errorMeta: tableError?.componentMeta,
        allMeta,
        currentSlug
      });
      alert('Cannot add item: Component configuration is missing. Please check the component settings.');
      return;
    }
    let emptyItem: any = {};

    metaToUse.fields.map((field: any) => {
      emptyItem[field.name] = null
    })

    const collection = getResolvedCollection();
    dispatch({
      type: ActionTypes.EDIT_ITEM_NEW,
      payload: {
        initial: emptyItem,
        slug: metaToUse.slug || currentSlug,
        fields: metaToUse.fields,
        collection,
        user: _user
      }
    })
    setShowEditor(true)
  }

  const handleOnChange = () => {
    setShowEditor(false)
    refreshList();
  }

  useEffect(() => {
    if (!showEditor) {
      registerItemEditorNavigationGuard(null)
      return
    }

    registerItemEditorNavigationGuard({
      hasUnsavedChanges: () => editItemRef.current?.hasUnsavedChanges?.() ?? false,
      discard: () => setShowEditor(false),
    })

    return () => {
      registerItemEditorNavigationGuard(null)
    }
  }, [showEditor])


  const deleteItem = async () => {
    const collection = getResolvedCollection();
    const slugToUse = currentSlug || currentMeta?.slug;
    try {
      const itemsToDelete = selectedRows.length > 0
        ? selectedRows.filter(item => item && item.uid)
        : currentItem?.uid ? [currentItem] : [];
      const uids = itemsToDelete.map(item => item.uid).filter(Boolean) as string[];

      setOpenDeleteDialog(false);
      setSelectedRows([]);

      (window as any).deleteTimeout = setTimeout(() => {
        if (uids.length > 0) {
          dispatch({
            type: ActionTypes.DELETE_ITEMS_BULK,
            payload: {
              slug: slugToUse,
              collectionName: collection,
              uids,
              organization: (currentMeta?.organization || routeOrgSegment || '').trim() || undefined,
            }
          });
        }
      }, 300);

    } catch (error) {
      console.error("Error during deletion:", error);
      setOpenDeleteDialog(false);
      setSelectedRows([]);
      setUpdateList(prev => !prev);
    }
  };

  const handleQuickAddEnter = () => {
    refreshList()
  }






  const tableViewLoading = useMemo(
    () =>
      computeTableViewLoadingState({
        currentSlug: (_currentSlug || currentSlug || '').trim(),
        tableLoadingKey: activeTableKey,
        routeCollectionSlug,
        routeOrgNorm,
        meta:
          meta && typeof meta === 'object' && !Array.isArray(meta)
            ? meta
            : _currentMeta,
        table:
          table && typeof table === 'object' && !Array.isArray(table)
            ? table
            : findTableEntryForRoute(
              allTables as { slug?: string; data?: unknown }[],
              activeTableKey,
              routeCollectionSlug,
              routeOrgNorm
            ),
        tableError,
        tableLoadingBySlug,
        forceLoadingTest,
      }),
    [
      _currentSlug,
      currentSlug,
      activeTableKey,
      meta,
      table,
      tableError,
      tableLoadingBySlug,
      forceLoadingTest,
      _currentMeta,
      allTables,
    ]
  )
  const { isLoading, isTableLoading, isRequestLoading, hasData } = tableViewLoading
  const showFullTableLoader = isTableLoading && !hasData
  const showTableRefreshing = isRequestLoading && hasData
  const showTableToolbar =
    componentActions.length > 0 ||
    (!createPermissionLoading && canCreate) ||
    (actionMode && componentActions.length > 0) ||
    bulkDeleteInProgress ||
    (hasData && selectedRows.length > 0)

  useLayoutEffect(() => {
    if (!showTableToolbar) {
      setTableToolbarHeight(0)
      return
    }
    const el = tableToolbarRef.current
    if (!el) {
      setTableToolbarHeight(0)
      return
    }
    const update = () => {
      setTableToolbarHeight(Math.ceil(el.getBoundingClientRect().height))
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [
    showTableToolbar,
    actionMode,
    bulkDeleteInProgress,
    selectedRows.length,
    componentActions.length,
    createPermissionLoading,
    canCreate,
    hasData,
  ])

  const rowSummaryFieldNames = useMemo(
    () => getRowSummaryFieldNamesFromMeta(meta || currentMeta),
    [meta, currentMeta]
  )

  const pickInformDetailFieldNames = useMemo(
    () => getRowSummaryFieldNamesFromMeta(meta || currentMeta, 12),
    [meta, currentMeta]
  )

  const duplicatesDialogRows = useMemo(
    () => (Array.isArray(filteredRows) ? (filteredRows as Record<string, unknown>[]) : []),
    [filteredRows]
  )

  const compareDialogRows = useMemo(() => {
    const seen = new Set<string>()
    const out: Record<string, unknown>[] = []
    for (const r of selectedRows) {
      const uid = typeof (r as { uid?: unknown }).uid === "string" ? (r as { uid: string }).uid : ""
      if (!uid || seen.has(uid)) continue
      seen.add(uid)
      out.push(r as Record<string, unknown>)
    }
    return out
  }, [selectedRows])

  const pickInformGroup = useMemo(
    () => pickInformGroupFromSelection(selectedRows),
    [selectedRows]
  )

  const showPickAndInform = useMemo(() => {
    if (!pickInformGroup || pickInformGroup.length < 2 || !currentSlug || actionMode) return false
    if (isItemPermissionPending("update")) return false
    for (const row of pickInformGroup) {
      const uid = (row as { uid?: string })?.uid
      if (!uid) return false
      const p = getItemPermissionFlags(String(uid))
      if (!p.canUpdate) return false
    }
    return true
  }, [
    pickInformGroup,
    currentSlug,
    actionMode,
    getItemPermissionFlags,
    isItemPermissionPending,
  ])

  const openPickInformDialog = useCallback(() => {
    if (!pickInformGroup || pickInformGroup.length < 2) return
    const first = (pickInformGroup[0] as { uid?: string })?.uid
    setPickInformKeepUid(first != null && String(first).trim() ? String(first).trim() : "")
    setPickInformPersonalNote("")
    setPickInformDialogOpen(true)
  }, [pickInformGroup])

  const confirmPickInform = useCallback(async () => {
    if (!pickInformGroup || pickInformGroup.length < 2 || !pickInformKeepUid || !currentSlug) return
    const keep = String(pickInformKeepUid).trim()
    if (!keep) return
    const archiveUids: string[] = []
    const seenArch = new Set<string>()
    for (const row of pickInformGroup) {
      const u = (row as { uid?: string })?.uid
      if (u == null) continue
      const s = String(u).trim()
      if (!s || s === keep || seenArch.has(s)) continue
      seenArch.add(s)
      archiveUids.push(s)
    }
    if (archiveUids.length === 0) return
    setPickInformBusy(true)
    try {
      const org = (currentMeta?.organization || routeOrgSegment || "").trim() || undefined
      const result = await pickAndInformSubmissionsAPI(
        currentSlug,
        keep,
        archiveUids,
        org,
        pickInformPersonalNote
      )
      toast.success(result.message || "Submission updated.")
      if (result.emailSkipped) {
        toast.info("No submitter email on file; notification was not sent.")
      } else if (result.emailError) {
        toast.warn(result.emailError)
      }
      setPickInformDialogOpen(false)
      setSelectedRows([])
      setUpdateList((p) => !p)
    } catch (e) {
      toast.error(formatErrorForDisplay(e))
    } finally {
      setPickInformBusy(false)
    }
  }, [
    pickInformGroup,
    pickInformKeepUid,
    pickInformPersonalNote,
    currentSlug,
    currentMeta?.organization,
    routeOrgSegment,
  ])

  const pickInformSelectedRow = useMemo((): Record<string, unknown> | null => {
    if (!pickInformGroup || !pickInformKeepUid) return null
    const keep = String(pickInformKeepUid).trim()
    if (!keep) return null
    for (const row of pickInformGroup) {
      const u = (row as { uid?: string })?.uid
      if (u != null && String(u).trim() === keep) {
        return row as Record<string, unknown>
      }
    }
    return null
  }, [pickInformGroup, pickInformKeepUid])

  const userInfoDialogFields = useMemo(() => {
    const uid = userInfoDialogUid
    if (!uid) return null
    const p = userInfoDialogProfile
    const c = userInfoDialogPreview
    const displayName =
      (p?.info?.name && String(p.info.name).trim()) ||
      [p?.firstName, p?.lastName].filter(Boolean).join(" ").trim() ||
      (c?.name && c.name.trim()) ||
      getUserUidDisplayLabel(uid) ||
      ""
    const displayEmail =
      (p?.email != null && String(p.email).trim()) || (c?.email && c.email.trim()) || ""
    const org =
      p?.organization != null && String(p.organization).trim()
        ? String(p.organization).trim()
        : ""
    return {
      uid,
      displayName,
      displayEmail,
      org,
      active: p?.active,
      admin: p?.admin,
      collectionCount: p?.collectionCount,
      collectionLimit: p?.collectionLimit,
    }
  }, [userInfoDialogUid, userInfoDialogProfile, userInfoDialogPreview])

  const userInfoDialogShowSpinner =
    userInfoDialogLoading &&
    !userInfoDialogProfile &&
    !(userInfoDialogPreview?.name || userInfoDialogPreview?.email)

  const showTableNotFoundPage =
    is404MongoError ||
    (Boolean(isSlugAccessError) && tableError?.type === "COMPONENT_NOT_FOUND")

  if (!isLoading && showTableNotFoundPage) {
    const kind = is404MongoError ? "collection_not_found" : "component_not_found"
    return (
      <div className="h-full min-h-0 flex flex-col p-4 md:p-6 overflow-auto">
        <NotFoundPage
          kind={kind}
          embedded
          title="Collection not found"
          availableSlugs={
            tableError?.type === "COMPONENT_NOT_FOUND" && tableError?.availableSlugs?.length
              ? tableError.availableSlugs
              : undefined
          }
          adminActions={
            is404MongoError && isAdmin ? (
              <Button
                className="bg-teal-600 hover:bg-teal-500 text-white"
                onClick={() => {
                  dispatch({ type: ActionTypes.CLEAR_TABLE_ERROR })
                  handleNewClick()
                }}
              >
                Add item to create collection
              </Button>
            ) : undefined
          }
        />
      </div>
    )
  }

  return (
    isLoading ?
      <ComponentTablePageLoading /> :
      <>
        <div className="h-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex flex-col overflow-hidden">
          {/* Error Banner */}
          {hasError && errorMessage && (
            <div className={`border-l-4 p-4 m-4 ${is404MongoError && isAdmin ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-400 dark:border-blue-500' : is404MongoError && !isAdmin ? 'bg-red-50 dark:bg-red-900/30 border-red-400 dark:border-red-500' : 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-400 dark:border-yellow-500'}`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {is404MongoError && isAdmin ? (
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  ) : is404MongoError && !isAdmin ? (
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="ml-3 flex-1">
                  {is404MongoError && isAdmin ? (
                    <>
                      <p className={`text-sm mb-2 ${is404MongoError && isAdmin ? 'text-blue-700 dark:text-blue-300' : ''}`}>
                        Collection not found. As an admin, you can create the collection by adding a new item.
                      </p>
                      <div className="mt-3">
                        <Button
                          onClick={() => {
                            dispatch({ type: ActionTypes.CLEAR_TABLE_ERROR });
                            handleNewClick();
                          }}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          Add New Item to Create Collection
                        </Button>
                      </div>
                    </>
                  ) : is404MongoError && !isAdmin ? (
                    <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                      Collection not found. Please contact an administrator to create this collection.
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2">
                        {errorMessage}
                      </p>
                      {tableError?.availableSlugs && tableError.availableSlugs.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-yellow-600 dark:text-yellow-400 mb-1">Available components:</p>
                          <div className="flex flex-wrap gap-2">
                            {tableError.availableSlugs.map((slug: string) => {
                              // Sanitize slug: remove trailing exclamation marks and trim whitespace
                              const sanitizedSlug = typeof slug === 'string' ? slug.replace(/[!]+$/, '').trim() : slug
                              return (
                                <a
                                  key={sanitizedSlug}
                                  href={buildCollectionPath({ slug: sanitizedSlug })}
                                  className="inline-flex items-center px-3 py-1 text-xs font-medium text-yellow-800 dark:text-yellow-200 bg-yellow-100 dark:bg-yellow-900/50 rounded-md hover:bg-yellow-200 dark:hover:bg-yellow-800/50 transition-colors"
                                >
                                  {sanitizedSlug}
                                </a>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {isSlugAccessError ? (
                    currentSlug ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                        Requested slug:{' '}
                        <span className="font-mono text-gray-700 dark:text-gray-200">{currentSlug}</span>
                      </p>
                    ) : null
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                      Component: <span className="font-semibold text-gray-700 dark:text-gray-200">{displayName}</span>
                      {currentSlug ? (
                        <span className="text-gray-400 dark:text-gray-500"> ({currentSlug})</span>
                      ) : null}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0 ml-4">
                  <button
                    onClick={() => dispatch({ type: ActionTypes.CLEAR_TABLE_ERROR })}
                    className={`inline-flex hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-md p-1 transition-colors ${is404MongoError && isAdmin ? 'text-blue-400 focus:ring-blue-500' : is404MongoError && !isAdmin ? 'text-red-400 focus:ring-red-500' : 'text-yellow-400 focus:ring-yellow-500'}`}
                    aria-label="Dismiss error"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Header Section - hidden when editor is shown */}
          {!showEditor && (
            <div className="flex-none px-4 pt-4 pb-4 md:px-6 md:pt-6 md:pb-5">
              <div className="flex flex-col gap-3 md:gap-4 md:flex-row md:items-center md:justify-between">
                <div className="min-w-0 flex flex-wrap items-center gap-3 gap-y-2">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 capitalize break-words">
                    {displayName}
                  </h1>
                  {archivedListMode === "all" ? (
                    <Badge
                      variant="outline"
                      className="shrink-0 text-sm font-semibold uppercase tracking-wide border-blue-500 text-blue-800 bg-blue-50 dark:border-blue-400 dark:text-blue-100 dark:bg-blue-950/60"
                    >
                      Showing active and archived
                    </Badge>
                  ) : archivedListMode === "only" ? (
                    <Badge
                      variant="outline"
                      className="shrink-0 text-sm font-semibold uppercase tracking-wide border-blue-500 text-blue-800 bg-blue-50 dark:border-blue-400 dark:text-blue-100 dark:bg-blue-950/60"
                    >
                      Archived only
                    </Badge>
                  ) : null}
                </div>
                <div className="hidden w-full flex-wrap items-center gap-3 md:w-auto md:justify-end sm:flex">
                  {!updatePermissionLoading && canUpdateCollection && (
                    <Button
                      variant="outline"
                      onClick={() => setExcelBulkUpdateOpen(true)}
                      className="flex items-center space-x-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      <span className="hidden sm:inline">Excel update</span>
                    </Button>
                  )}
                  {((!createPermissionLoading && canCreate) || hasData) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                          aria-label="Import and export"
                        >
                          {exportLoading ? (
                            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden />
                          ) : (
                            <ArrowDownUp className="h-4 w-4 shrink-0" aria-hidden />
                          )}
                          <span className="hidden sm:inline">Import / export</span>
                          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <TableImportExportMenuItems
                          canCreate={canCreate}
                          createPermissionLoading={createPermissionLoading}
                          hasData={hasData}
                          selectionCount={selectedRows.length}
                          exportLoading={exportLoading}
                          onImport={() => setImportDialogOpen(true)}
                          onExport={(scope, format) => openExportFieldDialog(scope, format)}
                        />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          "flex items-center gap-2",
                          archivedListMode !== "no"
                            ? "border-blue-600 bg-blue-50 text-blue-900 hover:bg-blue-100 hover:text-blue-950 dark:border-blue-500 dark:bg-blue-950/55 dark:text-blue-100 dark:hover:bg-blue-900/50 dark:hover:text-white"
                            : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                        )}
                        aria-label="Archived rows visibility"
                      >
                        <Archive className="h-4 w-4 shrink-0" aria-hidden />
                        <span className="hidden sm:inline">Archive</span>
                        <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <TableArchiveVisibilityMenuItems mode={archivedListMode} onSelect={setArchiveViewMode} />
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <DropdownMenu open={analyzeMenuOpen} onOpenChange={setAnalyzeMenuOpen}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="flex items-center space-x-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                      >
                        <Layers2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Analyze</span>
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      {hasActiveFilters ? (
                        <>
                          <DropdownMenuItem
                            onClick={() => {
                              setAnalyzeMenuOpen(false)
                              clearAllColumnFilters()
                            }}
                            className="gap-2"
                          >
                            <FilterX className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                            Clear {appliedFilterCount} filter{appliedFilterCount === 1 ? "" : "s"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      ) : null}
                      {showCollectionInfoButton ? (
                        <>
                          <DropdownMenuItem
                            onClick={() => {
                              setAnalyzeMenuOpen(false)
                              setCollectionInfoDialogOpen(true)
                            }}
                            className="gap-2"
                          >
                            <Info className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                            Collection info
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      ) : null}
                      <DropdownMenuItem
                        disabled={!hasData}
                        onClick={() => {
                          setAnalyzeMenuOpen(false)
                          setDuplicatesDialogOpen(true)
                        }}
                        className="gap-2"
                      >
                        <Copy className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                        Find duplicates…
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={!hasData || compareDialogRows.length < 2}
                        onClick={() => {
                          setAnalyzeMenuOpen(false)
                          setCompareDialogOpen(true)
                        }}
                        className="gap-2"
                      >
                        <GitCompare className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                        Compare selected…
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          setAnalyzeMenuOpen(false)
                          openActivityLog()
                        }}
                        className="gap-2"
                      >
                        <ScrollText className="h-4 w-4 shrink-0 opacity-70" />
                        Activity log
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {hasData && (
                    <>
                      {hasActiveFilters ? (
                        <>
                          <span className="hidden md:inline text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            {appliedFilterCount} filter{appliedFilterCount === 1 ? "" : "s"} applied
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 px-3 text-xs border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                            onClick={clearAllColumnFilters}
                          >
                            Clear filters
                          </Button>
                        </>
                      ) : null}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleOpenColumnSettings}
                        className="flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                        title="Configure column order and visibility"
                        aria-label="Configure column order and visibility"
                      >
                        <Settings2 className="h-4 w-4 shrink-0" />
                        <span className="hidden lg:inline">Columns</span>
                      </Button>
                    </>
                  )}
                  {debugMode && (
                    <Button
                      variant="outline"
                      onClick={() => setShowJsonDialog(true)}
                      className="flex items-center space-x-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                    >
                      <Code2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Raw JSON</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Content Section - Either Edit Form or Data View */}
          <div className="flex-1 flex flex-col min-h-0 px-4 md:px-6 pb-4 md:pb-6 overflow-hidden">
            {showEditor ? (
              /* Inline Edit Form */
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 h-full flex flex-col overflow-hidden mt-5">
                <EditItem ref={editItemRef} onEdited={handleOnChange} onCancel={() => setShowEditor(false)} />
              </div>
            ) : (
              /* Normal Data View */
              <>
                {/* Desktop Quick Add */}
                {showQuickAdd && (
                  <div className="hidden md:grid gap-6 mb-6 md:grid-cols-1">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Quick Add</h2>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Quickly add new items to your collection</p>
                        </div>
                      </div>
                      <QuickAdd onPressEnter={handleQuickAddEnter} />
                    </div>
                  </div>
                )}

                {/* Data Table Section with Internal Scrolling */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex-1 min-h-0 flex flex-col overflow-hidden mb-0 md:mb-0 mobile-data-container">
                  <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                    {showFullTableLoader ? (
                      <ComponentTableInlineDataLoading />
                    ) : (
                      <>
                        <div
                          ref={tableScrollRef}
                          onScroll={handleTableScroll}
                          className={`flex-1 overflow-auto overflow-x-auto min-h-0 ${showTableRefreshing ? '[&_table_tbody]:blur-sm [&_table_tbody]:pointer-events-none [&_table_tbody]:select-none [&_table_tbody]:transition-[filter] [&_table_tbody]:duration-200' : ''}`}
                        >
                          {showTableToolbar ? (
                            <div
                              ref={tableToolbarRef}
                              className="sticky top-0 left-0 z-20 shrink-0 bg-white dark:bg-gray-800 shadow-[0_1px_0_0_rgb(229_231_235)] dark:shadow-[0_1px_0_0_rgb(55_65_81)]"
                            >
                              {(componentActions.length > 0 || (!createPermissionLoading && canCreate)) && (
                                <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                                  <div className="flex items-center gap-2 min-w-0">
                                    {componentActions.length > 0 ? (
                                      <Button
                                        variant="default"
                                        type="button"
                                        onClick={() => setActionsDialogOpen(true)}
                                        className="flex items-center space-x-2 bg-green-600 text-white shadow-sm hover:bg-green-700 focus-visible:ring-green-600 dark:bg-green-600 dark:text-white dark:hover:bg-green-700 shrink-0"
                                      >
                                        <ListChecks className="w-4 h-4" />
                                        <span className="hidden sm:inline">Actions</span>
                                      </Button>
                                    ) : null}
                                  </div>
                                  <div className="flex items-center shrink-0">
                                    {!createPermissionLoading && canCreate ? (
                                      <Button
                                        variant="primary"
                                        type="button"
                                        onClick={() => handleNewClick()}
                                        className="bg-blue-600 text-white hover:bg-blue-700 px-3 sm:px-6 py-2 sm:py-2.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        <span className="font-medium hidden sm:inline">Add New Item</span>
                                      </Button>
                                    ) : null}
                                  </div>
                                </div>
                              )}
                              {actionMode && componentActions.length > 0 && (
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-3 py-2 border-b border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950">
                                  <div className="text-sm text-gray-600 dark:text-gray-400 min-w-0">
                                    <span className="font-medium text-gray-800 dark:text-gray-200">Action: </span>
                                    <span className="font-semibold text-gray-900 dark:text-gray-100">{selectedAction?.name}</span>
                                    <span className="text-gray-500 dark:text-gray-500 block sm:inline sm:ml-2">Click a row to apply.</span>
                                  </div>
                                  <Button
                                    type="button"
                                    onClick={() => setSelectedActionValue("")}
                                    className="px-4 bg-green-600 text-white hover:bg-green-700 shrink-0 w-full sm:w-auto"
                                  >
                                    Done
                                  </Button>
                                </div>
                              )}
                              {bulkDeleteInProgress && (
                                <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-sm font-medium">
                                  <Loader2 className="h-5 w-5 shrink-0 animate-spin" aria-hidden />
                                  <span>
                                    {bulkDeleteCount === 1
                                      ? 'Deleting 1 item...'
                                      : `Deleting ${bulkDeleteCount} items...`}
                                  </span>
                                  <span className="text-amber-600 dark:text-amber-300 font-normal">This may take a moment.</span>
                                </div>
                              )}
                              {hasData && selectedRows.length > 0 && (() => {
                                const bulkItem = selectedRows[0] ?? currentItem
                                const bulkPermissions = bulkItem
                                  ? getItemPermissionFlags(bulkItem?.uid)
                                  : { canRead: false, canUpdate: false, canDelete: false, canDelegate: false }
                                const canDeleteBulk = selectedRows.length > 1 ? canDeleteSelected : bulkPermissions.canDelete
                                const bulkDelegateVisible = bulkItem ? getItemDelegateActionsVisible(bulkItem) : false
                                const clearSelectionAfterAction = () => setSelectedRows([])
                                return (
                                  <div className="flex items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700 bg-white px-3 py-2 min-h-[44px] dark:bg-gray-800">
                                    <div className="flex min-w-0 items-center">
                                      <ActionMenu
                                        item={bulkItem}
                                        onDelete={() => bulkItem && handleDeleteClick(bulkItem)}
                                        onDetails={() => {
                                          if (bulkItem) handelShowDetailsClick(bulkItem)
                                          clearSelectionAfterAction()
                                        }}
                                        onEdit={() => {
                                          if (bulkItem) handleOnEdit(bulkItem)
                                          clearSelectionAfterAction()
                                        }}
                                        onDuplicate={() => {
                                          if (bulkItem) handleDuplicate(bulkItem)
                                          clearSelectionAfterAction()
                                        }}
                                        onArchive={handleArchiveClick}
                                        archiveDisabled={!canArchiveSelected}
                                        onRestore={handleRestoreClick}
                                        restoreDisabled={!canRestoreSelected}
                                        actions={componentActions}
                                        onRunAction={(action) => {
                                          applyUserActionToRowTargets(action, selectedRows)
                                        }}
                                        selectedRowsCount={selectedRows.length}
                                        permissions={{ ...bulkPermissions, canDelete: canDeleteBulk }}
                                        isItemPermissionPending={isItemPermissionPending}
                                        onDelegateOwner={() => bulkItem && openDelegateOwnerDialog(bulkItem)}
                                        onRemoveDelegatedOwner={() => bulkItem && requestRemoveDelegatedOwner(bulkItem)}
                                        showRemoveDelegatedOwner={Boolean(bulkItem && rowHasDelegatedOwner(bulkItem))}
                                        delegateActionsVisible={bulkDelegateVisible}
                                        showPickAndInform={showPickAndInform}
                                        onPickAndInform={openPickInformDialog}
                                        customTrigger={
                                          <span className="flex items-center gap-2 text-sm font-medium text-blue-800 dark:text-blue-300">
                                            {selectedRows.length} selected
                                            <ChevronDown className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" aria-hidden />
                                          </span>
                                        }
                                      />
                                    </div>
                                    {hasActiveFilters ? (
                                      <div className="flex shrink-0 items-center gap-2">
                                        <span className="hidden sm:inline text-xs font-medium text-blue-800 dark:text-blue-300 whitespace-nowrap">
                                          {appliedFilterCount} filter{appliedFilterCount === 1 ? "" : "s"}
                                        </span>
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className="h-8 px-2.5 text-xs shrink-0 border-blue-600 text-blue-700 hover:bg-blue-50 hover:text-blue-800 dark:border-blue-500 dark:text-blue-300 dark:hover:bg-blue-950/60 dark:hover:text-blue-200"
                                          onClick={clearAllColumnFilters}
                                        >
                                          Clear filters
                                        </Button>
                                      </div>
                                    ) : null}
                                  </div>
                                )
                              })()}
                            </div>
                          ) : null}
                          {hasData ? (
                            <DataTable
                              stickyHeaderTopPx={tableToolbarHeight}
                              columns={columns}
                              data={filteredRows ?? []}
                              onDoubleClick={handleOnEdit}
                              onDetails={handelShowDetailsClick}
                              onDuplicate={handleDuplicate}
                              onArchive={(item: any) => handleArchiveClick(item)}
                              onRestore={(item: any) => handleRestoreClick(item)}
                              onDelete={(item: any) => handleDeleteClick(item)}
                              dimArchivedRows={archivedListMode === "all"}
                              sortTableKey={activeTableKey}
                              page={page}
                              pageSize={pageSize}
                              paginateRows={false}
                              manualSorting
                              onSorted={handleOnSorted}
                              selectedRowsCount={selectedRows.length}
                              getItemPermissionFlags={getItemPermissionFlags}
                              isItemPermissionPending={isItemPermissionPending}
                              canDeleteSelected={canDeleteSelected}
                              actionMode={actionMode}
                              onRowClick={actionMode ? handleActionRowClick : undefined}
                              actionAppliedUid={actionFlashUid}
                              actionMenuActions={actionMenuActions}
                              selectedToolbarAction={actionMode ? selectedAction : null}
                              onApplyToolbarAction={actionMode ? handleApplyToolbarActionFromMenu : undefined}
                              onRunAction={handleRunUserAction}
                              actionInProgress={actionInProgress}
                              highlightUid={highlightUid}
                              rowSummaryFieldNames={rowSummaryFieldNames}
                              columnVisibility={columnVisibility}
                              onColumnVisibilityChange={setColumnVisibility}
                              getItemDelegateActionsVisible={getItemDelegateActionsVisible}
                              onDelegateOwner={openDelegateOwnerDialog}
                              onRemoveDelegatedOwner={requestRemoveDelegatedOwner}
                              rowHasDelegatedOwner={rowHasDelegatedOwner}
                              emptyTableTitle={
                                archivedListMode === "only" ? "No archived data available" : undefined
                              }
                              emptyTableDescription={
                                archivedListMode === "only"
                                  ? "Switch Archive to Default or Show all, or archive items from the row menu."
                                  : undefined
                              }
                              activeFilterColumnId={activeFilterColumnId}
                              onFormSubmissionShowUser={handleFormSubmissionShowUser}
                              onFormSubmissionShowForm={handleFormSubmissionShowForm}
                              onFormSubmissionShowLogs={handleFormSubmissionShowLogs}
                              onFormSubmissionImpersonateUser={
                                isAdmin ? handleFormSubmissionImpersonateUser : undefined
                              }
                              impersonatingSubmissionUid={isAdmin ? impersonatingSubmissionUid : null}
                              currentUserUid={authUser?.uid ?? null}
                              onShowInMongo={
                                isAdmin && isMongoDatasource && mongoInspectorCollection
                                  ? handleShowInMongoFromContext
                                  : undefined
                              }
                            />
                          ) : (
                            <div className="flex items-center justify-center p-8">
                              <div className="text-center space-y-4">
                                <p className="text-gray-500 dark:text-gray-400">
                                  {archivedListMode === "only" ? (
                                    <>
                                      No archived data available. Switch Archive to Default or Show all, or archive
                                      items from the row menu.
                                    </>
                                  ) : (
                                    <>
                                      No items found. Use the &quot;Add New Item&quot; button
                                      {showQuickAdd ? " or Quick Add below" : ""} to create your first item.
                                    </>
                                  )}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                        {hasData && Array.isArray(table?.data) && (() => {
                          const totalCount =
                            table?.pagination?.totalCount ?? table?.data?.length ?? 0
                          return (
                            <div className="flex-none flex items-center justify-between gap-4 border-t border-gray-200 dark:border-gray-700 px-4 py-2 bg-gray-50/50 dark:bg-gray-800/50 text-sm text-gray-600 dark:text-gray-400">
                              <div className="flex items-center gap-2">
                                <span>Rows per page</span>
                                <Select
                                  value={String(pageSize)}
                                  onValueChange={(v) => {
                                    const n = parseInt(v, 10)
                                    if (!Number.isNaN(n)) {
                                      setPageSize(n)
                                      handleTablePageChange(1)
                                    }
                                  }}
                                >
                                  <SelectTrigger className="w-[72px] h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {[10, 25, 50, 100, 500].map((n) => (
                                      <SelectItem key={n} value={String(n)}>
                                        {n}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="flex items-center gap-4">
                                <span>
                                  {totalCount === 0
                                    ? '0 rows'
                                    : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalCount)} of ${totalCount}`}
                                </span>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-8 w-8"
                                    disabled={page <= 1}
                                    onClick={() => {
                                      handleTablePageChange(Math.max(1, page - 1))
                                    }}
                                    aria-label="Previous page"
                                  >
                                    <ChevronLeft className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-8 w-8"
                                    disabled={totalCount === 0 || page >= Math.ceil(totalCount / pageSize)}
                                    onClick={() => {
                                      handleTablePageChange(page + 1)
                                    }}
                                    aria-label="Next page"
                                  >
                                    <ChevronRight className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )
                        })()}
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mobile Fixed QuickAdd at Bottom */}
        {showQuickAdd && (
          <div className="md:hidden fixed bottom-[10vh] left-0 right-0 z-50 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg mobile-quickadd-fixed">
            <div className="px-4 py-3 safe-area-pb">
              <QuickAdd onPressEnter={handleQuickAddEnter} />
            </div>
          </div>
        )}

        <Dialog
          open={actionsDialogOpen}
          onOpenChange={(open) => {
            setActionsDialogOpen(open)
            if (!open) {
              setActionsBulkConfirmAction(null)
            }
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {actionsBulkConfirmAction ? "Confirm apply to selection" : "Actions"}
              </DialogTitle>
              <DialogDescription>
                {actionsBulkConfirmAction ? (
                  <>
                    Apply{" "}
                    <span className="font-semibold text-foreground">
                      {actionsBulkConfirmAction.name}
                    </span>{" "}
                    to{" "}
                    <span className="font-semibold text-foreground">
                      {tableSelectionTargets.length}
                    </span>{" "}
                    selected {tableSelectionTargets.length === 1 ? "item" : "items"}?
                  </>
                ) : (
                  <>
                    {tableSelectionTargets.length > 0 ? (
                      <>
                        {tableSelectionTargets.length}{" "}
                        {tableSelectionTargets.length === 1 ? "item" : "items"} selected — choosing an action
                        will ask you to confirm before applying to all of them.{" "}
                      </>
                    ) : null}
                    With no selection, choose an action, then tap rows in the table to apply it.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            {actionsBulkConfirmAction ? (
              <>
                <p className="text-xs text-muted-foreground">
                  Rows you cannot edit are skipped.
                </p>
                <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full min-h-[48px] touch-manipulation sm:w-auto"
                    onClick={() => setActionsBulkConfirmAction(null)}
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    className="w-full min-h-[48px] touch-manipulation sm:w-auto"
                    disabled={tableSelectionTargets.length === 0}
                    onClick={() => {
                      if (!actionsBulkConfirmAction) return
                      applyUserActionToRowTargets(actionsBulkConfirmAction, tableSelectionTargets)
                      setSelectedActionValue("")
                      setActionsBulkConfirmAction(null)
                      setActionsDialogOpen(false)
                    }}
                  >
                    Apply to {tableSelectionTargets.length}{" "}
                    {tableSelectionTargets.length === 1 ? "item" : "items"}
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <div
                  className="max-h-[min(18rem,45vh)] overflow-y-auto overscroll-y-contain rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 shadow-inner"
                  role="listbox"
                  aria-label="Collection actions"
                >
                  {componentActions.map((action) => {
                    const value = action.uid ?? action.name
                    const isSelected = selectedActionValue === value
                    return (
                      <button
                        key={value}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={`w-full text-left px-4 py-3.5 min-h-[48px] flex items-center text-base font-medium border-b border-gray-200/90 dark:border-gray-700 last:border-b-0 touch-manipulation active:bg-gray-200 dark:active:bg-gray-800 hover:bg-gray-100/90 dark:hover:bg-gray-800/90 transition-colors ${isSelected ? "bg-indigo-100 dark:bg-indigo-950/55 text-indigo-950 dark:text-indigo-100" : "text-gray-900 dark:text-gray-100"}`}
                        onClick={() => {
                          if (tableSelectionTargets.length > 0) {
                            setActionsBulkConfirmAction(action)
                          } else {
                            setSelectedActionValue(value)
                            setActionsDialogOpen(false)
                          }
                        }}
                      >
                        <span className="break-words">{action.name}</span>
                      </button>
                    )
                  })}
                </div>
                <DialogFooter className="sm:justify-start gap-2">
                  {selectedActionValue ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSelectedActionValue("")
                        setActionsDialogOpen(false)
                      }}
                    >
                      Stop applying
                    </Button>
                  ) : null}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={pickInformDialogOpen}
          onOpenChange={(open) => {
            if (!pickInformBusy) {
              setPickInformDialogOpen(open)
              if (!open) setPickInformPersonalNote("")
            }
          }}
        >
          <DialogContent className="sm:max-w-2xl max-h-[min(90vh,640px)] flex flex-col">
            <DialogHeader>
              <DialogTitle>Pick and inform</DialogTitle>
              <DialogDescription>
                Selected rows are form submissions from the same form and the same submitter (creator, owner, or email).
                Choose which record to keep. All others are marked archived, and the submitter is emailed with a link to
                review submissions in their account.
              </DialogDescription>
            </DialogHeader>
            {pickInformGroup && pickInformGroup.length >= 2 ? (
              <div className="flex flex-col gap-4 py-2 min-h-0">
                <div className="grid grid-cols-1 gap-4 min-h-0 sm:grid-cols-2 sm:gap-6">
                  <div className="flex flex-col min-h-0 min-w-0">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Keep this submission</p>
                    <div className="space-y-2 overflow-y-auto max-h-[min(50vh,320px)] pr-1">
                      {pickInformGroup.map((row: Record<string, unknown>) => {
                        const uid = row?.uid != null ? String(row.uid).trim() : ""
                        if (!uid) return null
                        const checked =
                          String(pickInformKeepUid).trim() === uid
                        const submittedLabel = formatPickInformSubmittedAt(row)
                        return (
                          <label
                            key={uid}
                            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm transition-colors ${checked
                              ? "border-sky-500 bg-sky-50/80 dark:border-sky-600 dark:bg-sky-950/40"
                              : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900/40"
                              }`}
                          >
                            <input
                              type="radio"
                              name="pickInformKeep"
                              className="mt-1 shrink-0"
                              checked={checked}
                              onChange={() => setPickInformKeepUid(uid)}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-gray-900 dark:text-gray-100">{submittedLabel}</div>
                              {row._submitterEmail ? (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-all">
                                  {String(row._submitterEmail)}
                                </div>
                              ) : null}
                            </div>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                  <div className="flex flex-col min-h-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 px-4 pt-3 pb-2 border-b border-gray-200/80 dark:border-gray-700/80 shrink-0">
                      Details
                    </p>
                    <div className="overflow-y-auto flex-1 p-4 min-h-[12rem] max-h-[min(55vh,360px)]">
                      {!pickInformKeepUid ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Select a submission to preview its fields.</p>
                      ) : pickInformSelectedRow ? (
                        <div className="space-y-3 text-sm">
                          <div>
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Submitted</div>
                            <div className="text-gray-900 dark:text-gray-100 mt-0.5">
                              {formatPickInformSubmittedAt(pickInformSelectedRow)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Submitter email</div>
                            <div className="text-gray-900 dark:text-gray-100 mt-0.5 break-all">
                              {formatPickInformDetailValue(pickInformSelectedRow._submitterEmail)}
                            </div>
                          </div>
                          {pickInformSelectedRow._formUid ? (
                            <div>
                              <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Form</div>
                              <div className="font-mono text-xs break-all text-gray-900 dark:text-gray-100 mt-0.5">
                                {String(pickInformSelectedRow._formUid)}
                              </div>
                            </div>
                          ) : null}
                          <div>
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Updated</div>
                            <div className="text-gray-900 dark:text-gray-100 mt-0.5">
                              {formatPickInformDetailValue(
                                pickInformSelectedRow.updatedAt ??
                                pickInformSelectedRow._updated ??
                                (pickInformSelectedRow as { updated?: unknown }).updated
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Record ID</div>
                            <div className="font-mono text-xs break-all text-gray-500 dark:text-gray-400 mt-0.5">
                              {String(pickInformSelectedRow.uid ?? "—")}
                            </div>
                          </div>
                          {pickInformDetailFieldNames.length > 0 ? (
                            <>
                              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                                <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                                  Fields
                                </div>
                              </div>
                              {pickInformDetailFieldNames.map((name) => {
                                const raw = getFlexibleFieldValue(pickInformSelectedRow, name)
                                const label = fieldLabelByName[name] || name
                                return (
                                  <div key={name}>
                                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</div>
                                    <div className="text-gray-900 dark:text-gray-100 mt-0.5 break-words">
                                      {formatPickInformDetailValue(raw)}
                                    </div>
                                  </div>
                                )
                              })}
                            </>
                          ) : null}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Could not load details for this selection.</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-2 shrink-0">
                  <Label htmlFor="pick-inform-personal-note" className="text-sm font-medium">
                    Personal note (optional)
                  </Label>
                  <textarea
                    id="pick-inform-personal-note"
                    className={cn(
                      "flex min-h-[4.5rem] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                    )}
                    rows={3}
                    maxLength={4000}
                    placeholder="Shown to the submitter in the notification email"
                    value={pickInformPersonalNote}
                    onChange={(e) => setPickInformPersonalNote(e.target.value)}
                    disabled={pickInformBusy}
                  />
                </div>
              </div>
            ) : null}
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                disabled={pickInformBusy}
                onClick={() => setPickInformDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="button" disabled={pickInformBusy || !pickInformKeepUid} onClick={() => void confirmPickInform()}>
                {pickInformBusy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Working…
                  </>
                ) : (
                  "Confirm"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={collectionInfoDialogOpen} onOpenChange={setCollectionInfoDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Collection info</DialogTitle>
            </DialogHeader>
            <dl className="space-y-4 text-sm">
              <div>
                <dt className="text-muted-foreground font-medium">Collection ID</dt>
                <dd className="font-mono text-xs break-all mt-1 text-gray-900 dark:text-gray-100">
                  {String(collectionInfoModel.uid || "").trim() || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground font-medium">Creator ID</dt>
                <dd className="font-mono text-xs break-all mt-1 text-gray-900 dark:text-gray-100">
                  {normalizeCreatorId(collectionInfoModel) || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground font-medium">Owner ID</dt>
                <dd className="font-mono text-xs break-all mt-1 text-gray-900 dark:text-gray-100">
                  {normalizeCollectionOwnerId(collectionInfoModel) || "—"}
                </dd>
              </div>
            </dl>
          </DialogContent>
        </Dialog>

        <ComponentTableDataDialogs
          debugMode={debugMode}
          showJsonDialog={showJsonDialog}
          setShowJsonDialog={setShowJsonDialog}
          displayName={displayName}
          storeJson={storeJson}
          clientJson={clientJson}
          relationDialogOpen={relationDialogOpen}
          setRelationDialogOpen={setRelationDialogOpen}
          relationDialogTitle={relationDialogTitle}
          relationDialogLoading={relationDialogLoading}
          relationDialogData={relationDialogData}
          delegateTargetItem={delegateTargetItem}
          setDelegateTargetItem={setDelegateTargetItem}
          currentSlug={currentSlug}
          onOwnerDelegationChanged={onOwnerDelegationChanged}
          csvDialogOpen={csvDialogOpen}
          handleCsvDialogOpenChange={handleCsvDialogOpenChange}
          csvDialogFieldName={csvDialogFieldName}
          csvDialogInput={csvDialogInput}
          setCsvDialogInput={setCsvDialogInput}
          clearCsvForField={clearCsvForField}
          applyCsvDialog={applyCsvDialog}
          columnOrderDialogOpen={columnOrderDialogOpen}
          handleColumnOrderDialogOpenChange={handleColumnOrderDialogOpenChange}
          currentColumnOrderDraft={currentColumnOrderDraft}
          reorderableColumnIds={reorderableColumnIds}
          defaultColumnOrderIds={defaultColumnOrderIds}
          columnOrderScrollRef={columnOrderScrollRef}
          draggingColumnId={draggingColumnId}
          setDraggingColumnId={setDraggingColumnId}
          dragOverState={dragOverState}
          setDragOverState={setDragOverState}
          setColumnOrderDraft={setColumnOrderDraft}
          moveColumnInDraft={moveColumnInDraft}
          handleColumnDrop={handleColumnDrop}
          handleResetColumnOrder={handleResetColumnOrder}
          handleSaveColumnOrder={handleSaveColumnOrder}
          columnHiddenDraft={columnHiddenDraft ?? []}
          toggleColumnHiddenInDraft={toggleColumnHiddenInDraft}
          columnOrderVisibleCount={columnOrderVisibleCount}
          fieldLabelByName={fieldLabelByName}
          exportDialogOpen={exportDialogOpen}
          setExportDialogOpen={setExportDialogOpen}
          exportLoading={exportLoading}
          exportParams={exportParams}
          exportAllFields={exportAllFields}
          setExportAllFields={setExportAllFields}
          exportSelectedFields={exportSelectedFields}
          setExportSelectedFields={setExportSelectedFields}
          exportSemicolonField={exportSemicolonField}
          setExportSemicolonField={setExportSemicolonField}
          exportableFieldsFromMeta={exportableFieldsFromMeta}
          collectionHasRelationFields={collectionHasRelationFields}
          exportRelationMode={exportRelationMode}
          setExportRelationMode={setExportRelationMode}
          confirmExport={confirmExport}
          importDialogOpen={importDialogOpen}
          setImportDialogOpen={setImportDialogOpen}
          importExcelFields={(meta || currentMeta)?.fields}
          importOrganization={(currentMeta?.organization || routeOrgSegment || '').trim() || undefined}
          refreshList={refreshList}
          excelBulkUpdateOpen={excelBulkUpdateOpen}
          setExcelBulkUpdateOpen={setExcelBulkUpdateOpen}
          activityLogOpen={activityLogOpen}
          setActivityLogOpen={setActivityLogOpen}
          activityLogStackedItemDetailsOpen={showDetailsDrawer}
          onDismissActivityLogStackedItemDetails={() => setShowDetailsDrawer(false)}
          duplicatesDialogOpen={duplicatesDialogOpen}
          setDuplicatesDialogOpen={setDuplicatesDialogOpen}
          duplicatesDialogRows={duplicatesDialogRows}
          duplicatesMeta={meta}
          duplicatesCurrentMeta={currentMeta}
          onApplyDuplicateFilters={applyDuplicateResultFilters}
          compareDialogOpen={compareDialogOpen}
          setCompareDialogOpen={setCompareDialogOpen}
          compareDialogRows={compareDialogRows}
          activityLog={{
            loading: activityLogLoading,
            entries: activityLogEntries,
            total: activityLogTotal,
            page: activityLogPage,
            pageSize: activityLogPageSize,
            onPageChange: setActivityLogPage,
            actionFilter: activityLogActionFilter,
            onActionFilterChange: setActivityLogActionFilter,
            orderBy: activityLogOrderBy,
            onOrderByChange: setActivityLogOrderBy,
            orderDirection: activityLogOrderDirection,
            onOrderDirectionChange: setActivityLogOrderDirection,
            groupByUser: activityLogGroupByUser,
            onGroupByUserChange: setActivityLogGroupByUser,
            onOpenEntryItem: openActivityLogEntryItem,
            onListUserEntries: handleActivityLogListUser,
          }}
        />

        <Dialog
          open={formSubmissionUserDoc != null}
          onOpenChange={(open) => {
            if (!open) {
              setFormSubmissionUserDoc(null)
            }
          }}
        >
          <DialogContent className="max-w-[min(96vw,32rem)] dark:bg-gray-950">
            <DialogHeader>
              <DialogTitle>Validated user</DialogTitle>
              <DialogDescription>
                Submitter and ownership fields stored on this row (public form submission metadata).
              </DialogDescription>
            </DialogHeader>
            {formSubmissionUserDoc == null ? null : (
              <Table className="text-xs">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[40%] font-mono">Field</TableHead>
                    <TableHead className="font-mono">Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {validatedUserDetailRows(formSubmissionUserDoc).map((r) => (
                    <TableRow key={r.label}>
                      <TableCell className="align-top text-muted-foreground font-medium">{r.label}</TableCell>
                      <TableCell className="font-mono align-top whitespace-pre-wrap break-all">{r.value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormSubmissionUserDoc(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={userInfoDialogOpen} onOpenChange={handleUserInfoDialogOpenChange}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>User</DialogTitle>
              <DialogDescription>
                Account summary. Open the admin users list to view or edit the full record.
              </DialogDescription>
            </DialogHeader>
            <div className="relative min-h-[4rem] py-1">
              {userInfoDialogShowSpinner ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" aria-hidden />
                </div>
              ) : null}
              {userInfoDialogFields && !userInfoDialogShowSpinner ? (
                <dl className="space-y-2.5 text-sm">
                  {userInfoDialogFields.displayName ? (
                    <div className="grid grid-cols-[6.25rem_1fr] gap-x-3 gap-y-0.5 items-baseline">
                      <dt className="text-muted-foreground">Name</dt>
                      <dd className="break-words font-medium text-foreground">
                        {userInfoDialogFields.displayName}
                      </dd>
                    </div>
                  ) : null}
                  {userInfoDialogFields.displayEmail ? (
                    <div className="grid grid-cols-[6.25rem_1fr] gap-x-3 gap-y-0.5 items-baseline">
                      <dt className="text-muted-foreground">Email</dt>
                      <dd className="break-all text-foreground">{userInfoDialogFields.displayEmail}</dd>
                    </div>
                  ) : null}
                  <div className="grid grid-cols-[6.25rem_1fr] gap-x-3 gap-y-0.5 items-baseline">
                    <dt className="text-muted-foreground">User ID</dt>
                    <dd className="break-all font-mono text-xs text-foreground">{userInfoDialogFields.uid}</dd>
                  </div>
                  {userInfoDialogFields.org ? (
                    <div className="grid grid-cols-[6.25rem_1fr] gap-x-3 gap-y-0.5 items-baseline">
                      <dt className="text-muted-foreground">Organization</dt>
                      <dd className="break-words text-foreground">{userInfoDialogFields.org}</dd>
                    </div>
                  ) : null}
                  {userInfoDialogProfile && userInfoDialogFields.active !== undefined ? (
                    <div className="grid grid-cols-[6.25rem_1fr] gap-x-3 gap-y-0.5 items-baseline">
                      <dt className="text-muted-foreground">Active</dt>
                      <dd className="text-foreground">{userInfoDialogFields.active ? "Yes" : "No"}</dd>
                    </div>
                  ) : null}
                  {userInfoDialogProfile && userInfoDialogFields.admin !== undefined ? (
                    <div className="grid grid-cols-[6.25rem_1fr] gap-x-3 gap-y-0.5 items-baseline">
                      <dt className="text-muted-foreground">Admin</dt>
                      <dd className="text-foreground">{userInfoDialogFields.admin ? "Yes" : "No"}</dd>
                    </div>
                  ) : null}
                  {userInfoDialogProfile && userInfoDialogFields.collectionCount !== undefined ? (
                    <div className="grid grid-cols-[6.25rem_1fr] gap-x-3 gap-y-0.5 items-baseline">
                      <dt className="text-muted-foreground">Collections</dt>
                      <dd className="text-foreground">{String(userInfoDialogFields.collectionCount)}</dd>
                    </div>
                  ) : null}
                  {userInfoDialogProfile && userInfoDialogFields.collectionLimit !== undefined && userInfoDialogFields.collectionLimit !== null ? (
                    <div className="grid grid-cols-[6.25rem_1fr] gap-x-3 gap-y-0.5 items-baseline">
                      <dt className="text-muted-foreground">Collection limit</dt>
                      <dd className="text-foreground">
                        {userInfoDialogFields.collectionLimit === 0
                          ? "Unlimited"
                          : String(userInfoDialogFields.collectionLimit)}
                      </dd>
                    </div>
                  ) : null}
                </dl>
              ) : null}
            </div>
            <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleUserInfoDialogOpenChange(false)}
              >
                Close
              </Button>
              <Button type="button" onClick={handleShowUserInTable}>
                Show in users table
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {!showEditor && (
          <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 backdrop-blur dark:border-gray-800 dark:bg-gray-900/95 px-3 py-2 sm:hidden">
            <div className="mx-auto flex max-w-3xl items-center justify-between gap-1">
              <Button
                type="button"
                variant="ghost"
                className="flex-1 flex flex-col items-center justify-center h-auto py-1 px-2 text-xs text-gray-700 dark:text-gray-200"
                onClick={() => navigate("/")}
              >
                <Home className="h-5 w-5 mb-0.5" />
                <span className="text-[10px] leading-tight">Home</span>
              </Button>
              {((!createPermissionLoading && canCreate) || hasData) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      className="flex-1 flex flex-col items-center justify-center h-auto py-1 px-2 text-xs text-gray-700 dark:text-gray-200 touch-manipulation"
                      aria-label="Import and export"
                    >
                      {exportLoading ? (
                        <Loader2 className="h-5 w-5 mb-0.5 animate-spin shrink-0 text-muted-foreground" aria-hidden />
                      ) : (
                        <ArrowDownUp className="h-5 w-5 mb-0.5 shrink-0" aria-hidden />
                      )}
                      <span className="text-[10px] leading-tight">Data</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="top" align="center" className="w-56">
                    <TableImportExportMenuItems
                      canCreate={canCreate}
                      createPermissionLoading={createPermissionLoading}
                      hasData={hasData}
                      selectionCount={selectedRows.length}
                      exportLoading={exportLoading}
                      onImport={() => setImportDialogOpen(true)}
                      onExport={(scope, format) => openExportFieldDialog(scope, format)}
                    />
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className={cn(
                      "flex-1 flex flex-col items-center justify-center h-auto py-1 px-2 text-xs touch-manipulation rounded-lg",
                      archivedListMode !== "no"
                        ? "text-blue-800 bg-blue-50 dark:text-blue-100 dark:bg-blue-950/60"
                        : "text-gray-700 dark:text-gray-200"
                    )}
                    aria-label="Archived rows visibility"
                  >
                    <Archive className="h-5 w-5 mb-0.5 shrink-0" aria-hidden />
                    <span className="text-[10px] leading-tight">Archive</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="center" className="w-56">
                  <TableArchiveVisibilityMenuItems mode={archivedListMode} onSelect={setArchiveViewMode} />
                </DropdownMenuContent>
              </DropdownMenu>
              {componentActions.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1 flex flex-col items-center justify-center h-auto py-1 px-2 text-xs text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-950/50"
                  onClick={() => setActionsDialogOpen(true)}
                >
                  <ListChecks className="h-5 w-5 mb-0.5" />
                  <span className="text-[10px] leading-tight">Actions</span>
                </Button>
              )}
              {!updatePermissionLoading && canUpdateCollection && (
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1 flex flex-col items-center justify-center h-auto py-1 px-2 text-xs text-gray-700 dark:text-gray-200"
                  onClick={() => setExcelBulkUpdateOpen(true)}
                >
                  <FileSpreadsheet className="h-5 w-5 mb-0.5" />
                  <span className="text-[10px] leading-tight">Excel</span>
                </Button>
              )}
              {hasData && (
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1 flex flex-col items-center justify-center h-auto py-1 px-2 text-xs text-gray-700 dark:text-gray-200 touch-manipulation"
                  onClick={handleOpenColumnSettings}
                  aria-label="Configure column order and visibility"
                >
                  <Settings2 className="h-5 w-5 mb-0.5 shrink-0" aria-hidden />
                  <span className="text-[10px] leading-tight">Columns</span>
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="flex-1 flex flex-col items-center justify-center h-auto py-1 px-2 text-xs text-gray-700 dark:text-gray-200 touch-manipulation"
                    aria-label="Analyze"
                  >
                    <Layers2 className="h-5 w-5 mb-0.5 shrink-0" aria-hidden />
                    <span className="text-[10px] leading-tight">Analyze</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="center" className="w-52">
                  {hasActiveFilters && (
                    <>
                      <DropdownMenuItem
                        onClick={() => {
                          clearAllColumnFilters()
                        }}
                        className="gap-2"
                      >
                        <FilterX className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                        Clear {appliedFilterCount} filter{appliedFilterCount === 1 ? "" : "s"}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {showCollectionInfoButton ? (
                    <>
                      <DropdownMenuItem
                        onClick={() => setCollectionInfoDialogOpen(true)}
                        className="gap-2"
                      >
                        <Info className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                        Collection info
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  ) : null}
                  <DropdownMenuItem
                    disabled={!hasData}
                    onClick={() => setDuplicatesDialogOpen(true)}
                    className="gap-2"
                  >
                    <Copy className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                    Find duplicates…
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={!hasData || compareDialogRows.length < 2}
                    onClick={() => setCompareDialogOpen(true)}
                    className="gap-2"
                  >
                    <GitCompare className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                    Compare selected…
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => openActivityLog()} className="gap-2">
                    <ScrollText className="h-4 w-4 shrink-0 opacity-70" />
                    Activity log
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}

        <ComponentTableItemOverlays
          currentItem={currentItem}
          currentMeta={currentMeta}
          showDetailsDrawer={showDetailsDrawer}
          setShowDetailsDrawer={setShowDetailsDrawer}
          openDeleteDialog={openDeleteDialog}
          setOpenDeleteDialog={setOpenDeleteDialog}
          selectedRows={selectedRows}
          isItemPermissionPending={isItemPermissionPending}
          getItemPermissionFlags={getItemPermissionFlags}
          handleOnEdit={handleOnEdit}
          deleteItem={deleteItem}
          canDeleteSelected={canDeleteSelected}
        />
      </>

  )
}
export default ComponentTable
